/* `useRef` went with the manual re-send's `pushInFlight` and `pushIntent` refs on 2026-08-31 — this page holds no mutable push state any more. */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
/*
 * ⚠️ **`useLocation` is gone with the `fromDeal` router state it read.** This
 * page reads nothing about how somebody ARRIVED any more — see the block at the
 * top of the component. Re-importing it is the first step back to a notice built
 * on a navigation rather than on the record.
 */
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronIcon, PencilIcon, PlusIcon, TrashIcon } from '@/components/catalog/icons';
import { ImportedFromXeroBadge } from '@/components/documents/ImportedFromXeroBadge';
import { SentToClientBadge } from '@/components/documents/SentToClientBadge';
import { ApplyCreditButton } from '@/components/invoices/ApplyCreditButton';
import { InvoiceCreditSummary } from '@/components/invoices/InvoiceCreditSummary';
import { InlineInvoiceFields } from '@/components/invoices/InlineInvoiceFields';
import { useRaiseCreditNote } from '@/components/credit-notes/use-raise-credit-note';
import { InvoiceFormModal } from '@/components/invoices/InvoiceFormModal';
import {
  InvoiceLineItemsTable,
  type CompanySalesTax,
} from '@/components/invoices/InvoiceLineItemsTable';
import { InvoiceIcon, InvoiceMoney, StatusBadge } from '@/components/invoices/InvoiceBits';
import { ActivityButton, ActivityDrawer } from '@/components/tasks/ActivityDrawer';
import { XeroPanel } from '@/components/xero/XeroPanel';
import { XeroSalesDefaultsDialog } from '@/components/xero/XeroSalesDefaultsDialog';
import { DownloadPdfButton } from '@/components/documents/DownloadPdfButton';
import { isLegacyImported, isOverdue } from '@/lib/invoice-utils';
import { ApiError, api } from '@/lib/api';
/*
 * ⚠️ **THE GATE IS IMPORTED, NOT DECLARED — and `InvoicesPage` imports the same
 * one.** Read its doc before changing which invoices may be credited: it
 * enforces the owner's rule — any invoice that has been ISSUED, and not a
 * cancelled one — as the positive test `isAwaitingPayment || isPaid`.
 */
import { creditNoteGateOf } from '@/lib/credit-notes';
/*
 * ⚠️ **`CONVERTED_LINES_HAVE_NO_TAX` IS NO LONGER IMPORTED, because it no longer
 * EXISTS.** It is deleted from `@/lib/deal-conversion` rather than reworded: a
 * copied line takes its tax rate from the company now, and the two conversion
 * routes no longer have one true sentence between them (the quote route can
 * still land at `0.00`; this one cannot, because the tax refusal fires first).
 * The replacements are `CONVERTED_LINE_TAX_RATE` and
 * `CONVERTED_INVOICE_LINE_TAX_RATE`, and both belong on the CONVERSION forms,
 * beside the lines they describe — not on the document afterwards, where the
 * grid below already shows every line's real rate.
 */
import {
  exchangeRateOf,
  formatRateLine,
  toBaseCurrency,
  type ExchangeRate,
} from '@/lib/currency-conversion';
import { denialReason } from '@/lib/permissions';
/*
 * The "the customer's copy shows a different total" fact. ONE module for both
 * detail pages — read its header before touching the wording, and never test
 * anything but `repricedAt` to decide whether to render it.
 */
import { repriceNotice } from '@/lib/reprice';
// The demarcation (owner 2026-10-25): an invoice is Xero-synced, so where it
// names its customer it shows the Billing name. Links still go to the company.
import { documentCustomerName } from '@/lib/company-names';
import { formatDateOnly, formatDateTime, pluralise, documentDisplayTitle} from '@/lib/format';
import { attachmentsSentence } from '@/lib/record-delete';
import { viewerZone } from '@/lib/task-time';
import type { Company, Invoice, InvoiceProduct, InvoiceStatus } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { cityLabel } from '@/lib/use-project-options';
/*
 * ⚠️ **FIVE IMPORTS LEFT THIS FILE ON 2026-08-31, ALL WITH THE MANUAL RE-SEND.**
 * `pushIntentFor` and `XeroPushIntent` minted and typed the branded intent the
 * removed "Send to Xero now" required; `autoPushRefusalOutcome` and
 * `xeroErrorLines` were read only inside `classifyPushFailure`, which is
 * deleted; `isLinked` decided between two next-step sentences that are now one
 * sentence.
 *
 * ⚠️ This page makes NO Xero request at all now — it only reports what the
 * SERVER did inside a save. Re-importing `pushIntentFor` here is the first step
 * back to a push on this screen; do not do it without the owner.
 *
 * ⚠️ `isSilentlyNotSyncing` ARRIVED in the same change, and it is not the
 * removed intent under another name: it asks *will a SAVE reach Xero*, which is
 * the only question left once the button has gone. See `saveWillReachXero`.
 */
import {
  autoPushOutcomeOf,
  isSilentlyNotSyncing,
  linkOf,
  type XeroLink,
  type XeroPushReport,
} from '@/lib/xero';
import { useXero } from '@/lib/xero-context';
import { usePageLayout } from '@/lib/use-page-layout';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Modal,
  PageHeader,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  Skeleton,
} from '@/components/ui';
import { Money } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';

/* --- small shared bits --------------------------------------------------- */

function BackToInvoices() {
  return (
    <Link
      to="/invoices"
      className="inline-flex items-center gap-1 rounded-sm text-xs text-fg-muted transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none"
    >
      <ChevronIcon direction="left" />
      All invoices
    </Link>
  );
}

function DetailItem({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-fg-secondary [overflow-wrap:anywhere]">
        {children}
        {hint && <span className="mt-0.5 block text-xs text-fg-muted">{hint}</span>}
      </dd>
    </div>
  );
}

function DetailGroup({
  title,
  columns = 'sm:grid-cols-2 lg:grid-cols-4',
  children,
}: {
  title: string;
  columns?: string;
  children: ReactNode;
}) {
  return (
    <PanelBody className="border-b border-border last:border-b-0">
      <h3 className="mb-3 text-sm font-semibold text-fg">
        {title}
      </h3>
      <dl className={`grid gap-x-4 gap-y-3 ${columns}`}>{children}</dl>
    </PanelBody>
  );
}

/*
 * Every relationship on an invoice is a LEFT JOIN, null on all seven Zoho
 * imports (no column existed) — expected, not an error, so it reads as
 * "Unassigned".
 */
function LinkedValue({ to, name }: { to: string | undefined; name: string | undefined }) {
  // Both halves or neither: a null relation has no id, and `/companies/undefined` is a link to a 404.
  if (!to || !name) return <span className="text-fg-subtle">Unassigned</span>;
  return (
    <TextLink to={to}>
      {name}
    </TextLink>
  );
}

/* --- loading shapes ------------------------------------------------------ */

function DetailsSkeleton() {
  return (
    <Panel>
      <PanelHeader title="Invoice details" />
      <PanelBody>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} className="grid gap-1.5">
              <Skeleton className="h-2.5 w-20" />
              <Skeleton className="h-3.5 w-32" />
            </div>
          ))}
        </div>
      </PanelBody>
    </Panel>
  );
}

/* --- the base-currency figure, and the rate it rests on ------------------ */

/*
 * What this invoice is worth in the Xero organisation's own currency —
 * mirrors the block on `QuoteDetailPage`; anything corrected here belongs
 * there too.
 *
 * ⚠️ Nothing here computes money. `toBaseCurrency` in
 * `src/lib/currency-conversion.ts` does the arithmetic on decimal strings;
 * `Number()` on money is forbidden everywhere in this repo.
 *
 * ⚠️ A stored rate is a HISTORICAL FACT, not a live one — `currencyRate` is
 * the rate PINNED to this invoice, pushed to Xero verbatim as
 * `Invoice.CurrencyRate`. There is no "refresh the rate" control: rendering
 * today's rate would silently restate what a customer was billed.
 *
 * Five outcomes, three of which must show no number at all:
 *
 *  - `none`          — already the base currency, or base currency UNKNOWN
 *                      (Xero not connected). ⚠️ Never assume GBP.
 *  - `no-rate`       — no rate pinned. Every invoice in production today. No
 *                      fabricated `0.00`, no fallback to a rate of 1 (parity).
 *  - `rate-only`     — rate pinned, no grand total to apply it to.
 *  - `unusable-rate` — rate pinned but unusable (e.g. stored `0.000000`),
 *                      called out rather than folded into `no-rate`.
 *  - `converted`     — both figures exist.
 */
type BaseCurrencyView =
  | { kind: 'none' }
  | { kind: 'no-rate'; baseCurrency: string }
  | { kind: 'unusable-rate'; baseCurrency: string; recorded: string }
  | { kind: 'rate-only'; rate: ExchangeRate }
  | { kind: 'converted'; rate: ExchangeRate; amount: string };

/*
 * ⚠️ `exchangeRateOf` is the only sanctioned way to build an `ExchangeRate` —
 * this function does not hand-build one. Holding the type means "there is a
 * real conversion to display", which is what lets `formatRateLine` and
 * `toBaseCurrency` stay total; assembling one here from a raw `currencyRate`
 * could slip a zero or malformed rate past that check.
 *
 * `exchangeRateOf` returns `null` for five unrelated reasons; the null case is
 * re-examined below, against the same inputs, purely to decide what may be
 * said — never to reconstruct a rate it refused.
 */
function baseCurrencyView(invoice: Invoice, baseCurrency: string | null): BaseCurrencyView {
  const rate = exchangeRateOf({
    baseCurrency,
    documentCurrency: invoice.currency,
    currencyRate: invoice.currencyRate,
  });

  if (rate !== null) {
    /*
     * The GRAND total only, matching how Xero converts just the document foot:
     * rounding once at the end vs. rounding four totals separately can
     * disagree by a penny, so a converted sub total/discount/tax wouldn't
     * always add up to the converted grand total.
     */
    const amount = toBaseCurrency(invoice.grandTotal, rate);
    // `null` here means the amount didn't convert, not a bad rate (already validated) — the rate is still worth showing.
    return amount === null ? { kind: 'rate-only', rate } : { kind: 'converted', rate, amount };
  }

  // Xero disconnected, connection read unsettled, or invoice has no currency — base currency UNKNOWN in all three; silence is the only honest rendering. ⚠️ Never assume GBP.
  if (baseCurrency === null || invoice.currency === null) return { kind: 'none' };
  // Already in the base currency — compared exactly (no case-folding), matching `exchangeRateOf`.
  if (baseCurrency === invoice.currency) return { kind: 'none' };
  /*
   * Cross-currency, no usable rate — kept apart: `null` is the ordinary state
   * (every invoice in production today); a present-but-unusable rate is a
   * fault in the record and must not read as "not recorded".
   */
  if (invoice.currencyRate === null) return { kind: 'no-rate', baseCurrency };
  return { kind: 'unusable-rate', baseCurrency, recorded: invoice.currencyRate };
}

/*
 * ⚠️ Why `no-rate` says more than "Not recorded": every invoice in production
 * carries `currencyRate: null`, so a bare "Not recorded" would read as
 * missing data or a half-finished feature. The extra sentence heads that off
 * — this is the ordinary state, and the rate is Xero's to apply, not ours to
 * store. `rate-only`/`converted` clarify the rate isn't live, for the same
 * reason.
 */
function rateHint(view: BaseCurrencyView): string | null {
  switch (view.kind) {
    case 'none':
      return null;
    case 'no-rate':
      return `No rate pinned, so no ${view.baseCurrency} figure. Xero applies its own on the day.`;
    case 'unusable-rate':
      return `Not a usable rate, so no ${view.baseCurrency} figure is shown.`;
    case 'rate-only':
    case 'converted':
      return 'Pinned to this invoice — not today’s rate.';
  }
}

/* --- the Xero link envelope --------------------------------------------- */

/*
 * ⚠️ Read the link with `linkOf(invoice)`, never from `xeroInvoiceId` — that
 * column is writable and written by nothing (`types.ts` calls it reserved),
 * so `null` there is not evidence of anything (same trap as `xeroQuoteId` on
 * the quote page). `Invoice.xero` is a REQUIRED `XeroLink`, so `linkOf` is the
 * single reader; it falls back to `NO_LINK` for a response predating that.
 */

/*
 * ⚠️ Quotes have no such state — this compares OUR record against the last
 * push, which nothing else does anywhere (built here, not in `@/lib/xero`,
 * since it's one page's reading, not yet a shared rule).
 *
 * ⚠️ **CORRECTED 2026-08-31.** This continued: *"`capabilities.sync` is false,
 * so `XeroPanel`'s footer offers nothing here but Unlink; the re-send route
 * this strip offers (off `pushIntentFor()`'s intent) is still the only one for
 * an edited, already-pushed invoice."* **There is no re-send route.** The
 * owner removed the manual push from quotes and invoices on 2026-08-31, having
 * asked twice. This reading now ends in a SENTENCE and no button: it says Xero
 * is behind, and saving the invoice again is what brings it up to date.
 *
 * 🔴 **Which is the whole gap: an invoice whose push failed and which nobody
 * edits again stays stale in Xero, and this strip is the only thing that will
 * ever say so.** That is why the reading is kept rather than removed with the
 * button it used to feed.
 *
 * ⚠️ The tolerance below is NOT a fudge — removing it makes this useless. The
 * push stamps `lastSyncedAt` and THEN writes `xero_invoice_number` back onto
 * `d_invoices`, whose `updated_at` carries `ON UPDATE CURRENT_TIMESTAMP`, so
 * `updatedAt` is always a few milliseconds later than `lastSyncedAt` after a
 * push — a bare `>` would flag every freshly pushed invoice as having unsent
 * changes. Accepted cost: an edit within a minute of a push goes unreported,
 * the lesser evil against an indicator that cries wolf on every record. Real
 * fix is on the backend (stamp `lastSyncedAt` after the number write), and
 * this constant goes.
 */
const PUSH_TRAILING_WRITE_TOLERANCE_MS = 60_000;

/**
 * Milliseconds since the epoch, or `null` for anything that isn't a real
 * instant (never a bare `YYYY-MM-DD`, which this repo forbids constructing a
 * `Date` from) — `null` propagates into "say nothing" rather than a
 * comparison against a nonsense number.
 */
function instantOf(value: string | null): number | null {
  if (value === null) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Has this invoice been edited since it was last sent to Xero, and when?
 *
 * `null` for everything unknowable (never pushed, no `lastSyncedAt`, an
 * unparseable timestamp) — "we cannot tell" and "it is up to date" look
 * identical on screen, and silence is the honest rendering of the first.
 */
function changedSinceLastPush(invoice: Invoice, link: XeroLink): { sentAt: string } | null {
  if (link.id === null || link.lastSyncedAt === null) return null;
  const synced = instantOf(link.lastSyncedAt);
  const updated = instantOf(invoice.updatedAt);
  if (synced === null || updated === null) return null;
  if (updated - synced <= PUSH_TRAILING_WRITE_TOLERANCE_MS) return null;
  return { sentAt: link.lastSyncedAt };
}

/* --- reporting what the SAVE did with Xero ------------------------------ */

/*
 * ⚠️ **THIS PAGE NO LONGER PUSHES TO XERO AT ALL. IT ONLY REPORTS.**
 *
 * ⚠️ **CORRECTED 2026-08-31**, and the correction takes the whole section
 * heading with it (it read *"keeping the LINKED Xero document in step"*). The
 * block here argued at length that the MANUAL RE-SEND below was deliberately
 * kept and that `pushIntentFor`'s `'update'`-only filter was what stopped it
 * becoming a second create path. In full, the load-bearing half:
 *
 * > *"What `pushIntentFor` still guards is the **manual re-send** below, which
 * > is a person pressing a button and is deliberately kept … ⚠️ **That makes
 * > the `'update'`-only filter below MORE load-bearing, not less.** It used to
 * > be a courtesy — the create was one panel away. It is now the reason this
 * > button cannot be the second create path the removal exists to close, and
 * > the endpoint behind it is still create-**or**-update."*
 *
 * **There is no button, so there is nothing left for that filter to guard.**
 * Owner's decision, 2026-08-31, asked twice: *"remove this Push to Xero button
 * from both invoices and quotes, because auto saving is working."* The ref, the
 * effect that filled it, `sendToXeroNow` and `classifyPushFailure` are all
 * deleted — a dead push path is read as a live one by the next person to grep
 * for one — and `pushIntentFor` is no longer imported by this file.
 *
 * **What survives, and is the reason the rule was ever written:** the server
 * enforces update-never-create itself, inside every save, on every writer —
 * this page, a line-item edit, an importer, a script and any second client.
 * The structural guarantee here could only ever have covered one screen.
 *
 * 🔴 **THE GAP THIS LEAVES.** There is now no manual retry for an invoice whose
 * automatic push failed. The way to retry is to SAVE the invoice again, and a
 * save re-pushes. But an invoice whose push failed and which nobody edits again
 * **stays stale in Xero with nothing on screen offering to resend it** —
 * {@link pushNoticeOf} says so and offers no control. Accepted on the owner's
 * decision, not overlooked. `POST /xero/create/invoices/{id}` is unchanged and
 * remains the operator recovery path.
 *
 * ⚠️ The local save has ALREADY SUCCEEDED by the time any of this is read — no
 * failure here may be worded as the edit having failed. **That matters more
 * now, not less:** Xero does not enforce invoice-number uniqueness, so a person
 * who reads a failed push as a failed save and saves again is the path to two
 * live demands for money against one customer. {@link pushNoticeOf} leads with
 * the record being saved on every single branch.
 */

/**
 * Codes after which this invoice is terminal for Xero, and which the shared
 * model does not already classify.
 *
 * Not a retry policy — a statement about the document: a payment has landed
 * against it, the server's `assertStillWritable` refuses before writing
 * anything, and it will refuse identically on every future attempt.
 *
 * ⚠️ It used to end *"Offering a Send button would spend the tenant's rate
 * budget to reproduce the same sentence."* There is no Send button on any
 * branch now; what this set still decides is the TONE and the WORDING — a
 * `stopped` outcome must not read as something a person can put right.
 */
const XERO_PUSH_STOP_CODES: ReadonlySet<string> = new Set(['XERO_INVOICE_HAS_PAYMENTS']);

/** Facts about the CONNECTION, not about this invoice. They route to `/integrations`. */
const XERO_PUSH_CONNECTION_CODES: ReadonlySet<string> = new Set([
  'XERO_NOT_CONNECTED',
  'XERO_NOT_CONFIGURED',
  'XERO_REAUTH_REQUIRED',
  'XERO_TOKEN_REJECTED',
]);

/**
 * The two refusals the Sales-defaults dialog this page already owns can fix.
 * The same pair `XeroPanel` offers `onFixCompanyDefaults` for — one vocabulary,
 * not two.
 */
const XERO_PUSH_DEFAULTS_CODES: ReadonlySet<string> = new Set([
  'XERO_NO_TAX_TYPE',
  'XERO_NO_LINE_AMOUNT_TYPE',
]);

/**
 * Where a push got to. None of these may be confused with the SAVE, which has
 * already succeeded in every one of them.
 *
 * Mirrors {@link XeroAutoPushOutcome} under different names, deliberately —
 * see `classifyPushReport`. `idle` has no quote-side counterpart: this page
 * also reads a stale-version state off the link when nothing has been pushed
 * in this session.
 *
 * ⚠️ **CORRECTED 2026-08-30: `queued` and `sending` are GONE.** They described
 * a two-second debounce this page ran before making its own `api.pushToXero`
 * call after every save. The server pushes inside the save now, so there is no
 * window between the two to report on — the push is finished by the time the
 * response arrives. The debounce, its four refs and `XERO_PUSH_DEBOUNCE_MS`
 * went with them.
 *
 * ⚠️ **CORRECTED AGAIN 2026-08-31 — `sending` IS GONE TOO.** That paragraph
 * ended: *"`sending` survives only for the MANUAL re-send, which is still a
 * second request this page makes and still has a gap to name."* The manual
 * re-send was removed on the owner's decision, so this page makes no Xero
 * request of any kind and there is no gap left to name. Every member below is
 * now something the SERVER reported about a save that had already finished.
 */
type XeroAutoPush =
  | { kind: 'idle' }
  | { kind: 'sent'; at: string }
  /**
   * ⚠️ **Nothing was sent, and nothing is wrong.** The server declined to call
   * Xero at all — no lines yet, no linked company, or the invoice has never
   * been sent to Xero, which is the answer to every save of every invoice that
   * is not in Xero yet. See {@link XeroAutoPushOutcome}, where the reasoning
   * lives; it must never be rendered as a refusal.
   */
  | { kind: 'skipped'; message: string }
  /** Xero's copy has left DRAFT. The ORDINARY outcome, and never a fault. */
  | { kind: 'settled'; message: string }
  /** The Xero document was deleted there; the link has been stood down. */
  | { kind: 'gone'; message: string }
  /** Terminal for another reason. The invoice is Xero's to maintain now. */
  | { kind: 'stopped'; message: string }
  /** Xero refused, and somebody can do something about it. */
  | { kind: 'refused'; message: string; offerDefaults: boolean; offerIntegrations: boolean }
  /** Xero gave no answer we can act on. Nothing is wrong with the invoice. */
  | { kind: 'unreachable'; message: string };

/** Shared so the derived "no push attempted here" reading keeps a stable identity. */
const AUTO_PUSH_IDLE: XeroAutoPush = { kind: 'idle' };

/**
 * Classify what the SERVER said it did with Xero while saving this invoice.
 *
 * ⚠️ **THE ONLY ENTRY POINT, since 2026-08-31.** This read:
 *
 * > *"⚠️ **The other entry point to the same vocabulary**, and the reason both
 * > exist is that they are fed by different things: `classifyPushFailure` reads
 * > a thrown `ApiError` from the manual re-send, this reads the `xeroPush`
 * > report on a response that succeeded. They share `autoPushRefusalOutcome`,
 * > so the two can never come to mean different things by the same Xero code."*
 *
 * `classifyPushFailure` is deleted with the manual re-send it was fed by, so
 * there is one path in and nothing left for it to agree with. Every state this
 * page can show is now something the server reported about a save.
 *
 * ⚠️ **`idle` is what "the server said nothing" maps to**, and it is right
 * rather than a fallback: `idle` is this page's "nothing has been pushed in
 * this session", which is exactly true of a server that predates `xeroPush`.
 * The stale-version reading off the link then decides what, if anything, is
 * said — which is what this page did before any of this existed.
 *
 * ⚠️ The two routings that read an `ApiError`'s STATUS — a transient upstream
 * failure and any other `5xx` — are not available here: a `xeroPush` report has
 * no status, because the request it rode in on returned `200`. Both would
 * arrive as an ordinary `refused` carrying the server's own sentence, which is
 * accurate and one routing poorer. The CONNECTION codes are keyed on the code
 * and are checked below, so that half is not lost.
 *
 * ⚠️ **`XERO_PUSH_TRANSIENT_CODES` went with `classifyPushFailure`** — it was
 * read at exactly one site, inside it, on a status this path never has. If a
 * transient/`5xx` split is ever wanted here it has to be rebuilt off the CODE
 * alone; do not restore the set and assume it fires.
 */
function classifyPushReport(report: XeroPushReport | undefined): XeroAutoPush {
  const outcome = autoPushOutcomeOf(report);
  if (outcome === null) return { kind: 'idle' };
  switch (outcome.kind) {
    case 'updated':
      return { kind: 'sent', at: new Date().toISOString() };
    case 'skipped':
      return { kind: 'skipped', message: outcome.reason };
    case 'settled':
      return { kind: 'settled', message: outcome.messages.join(' ') };
    case 'gone':
      return { kind: 'gone', message: outcome.messages.join(' ') };
    case 'refused': {
      const message = outcome.messages.join(' ');
      // Terminal either way — the local set names codes the shared map has not been taught; `nowhere` catches the ones it has.
      if (XERO_PUSH_STOP_CODES.has(outcome.code) || outcome.refusal?.destination === 'nowhere') {
        return { kind: 'stopped', message };
      }
      return {
        kind: 'refused',
        message,
        // The same two sets the manual path uses, so one Xero code cannot offer a different button depending on how it arrived.
        offerDefaults: XERO_PUSH_DEFAULTS_CODES.has(outcome.code),
        offerIntegrations: XERO_PUSH_CONNECTION_CODES.has(outcome.code),
      };
    }
    case 'unreachable':
      return { kind: 'unreachable', message: outcome.messages.join(' ') };
  }
}

/**
 * ONE strip, saying where this invoice stands with Xero — never allowed to
 * read "synced" while it is not.
 */
interface PushNotice {
  /**
   * ⚠️ `neutral` is the absence of a warning, for `settled` alone — an
   * invoice that has left DRAFT refuses every later push by design, and a
   * warning on every save afterwards would train people to disregard the
   * strip entirely, taking the refusals that DO need acting on with it.
   */
  tone: 'neutral' | 'info' | 'warning' | 'success';
  title: string;
  body: string;
  /**
   * ⚠️ **`offerSend` IS GONE, 2026-08-31.** It read: *"The manual send, which
   * is the ONLY re-send route this app has for a linked invoice."* The owner
   * removed the manual push from quotes and invoices, having asked twice, so
   * there is no Send button for a notice to offer.
   *
   * 🔴 **Nothing replaces it.** Every branch that used to end in a button now
   * ends in a sentence saying that saving the invoice again is what sends it.
   * An invoice whose push failed and which nobody edits again stays stale in
   * Xero. Accepted, not overlooked.
   *
   * The two below survive because neither is a push: one opens a dialog on the
   * COMPANY, one navigates to `/integrations`. Both fix the cause; the save is
   * then what sends the invoice.
   */
  offerDefaults: boolean;
  offerIntegrations: boolean;
}

/**
 * ⚠️ The `canSend` parameter is gone with `offerSend`. It documented: *"Whether
 * a push would be accepted by `pushIntentFor` RIGHT NOW. Every `offerSend` is
 * `&&`-ed with it, since the link can move under this strip — a refusal that
 * stands it down to `archived` would otherwise leave a Send button that
 * silently declines."* There is no button to keep honest.
 */
function pushNoticeOf(
  state: XeroAutoPush,
  unsent: { sentAt: string } | null,
  updatedAt: string,
): PushNotice | null {
  switch (state.kind) {
    case 'idle':
      // Nothing attempted in this session — the stale-version reading is all there is to go on.
      if (unsent === null) return null;
      return {
        tone: 'warning',
        title: 'This invoice has changed since it was sent to Xero.',
        /*
         * ⚠️ **CORRECTED 2026-08-31.** This ended *"Send it again to match."*,
         * naming a button that has been removed — copy attached to a control is
         * falsified when the control goes, and it fails in the direction nobody
         * reports: it reads as advice, and the reader hunts for something that
         * is not there. Saving is the send now.
         */
        body: `Sent ${formatDateTime(unsent.sentAt)}, edited ${formatDateTime(updatedAt)}. Save it again to send the latest version.`,
        offerDefaults: false,
        offerIntegrations: false,
      };
    /*
     * ⚠️ **THE `sending` CASE WAS HERE AND IS DELETED, 2026-08-31.** It read
     * *"Sending this change to Xero… / Saved here; the update is on its way."*,
     * under a comment saying *"Only the MANUAL re-send reaches this now."* The
     * manual re-send is gone, so nothing produces the state; the union member
     * went with it, which is what makes this a compile error rather than an
     * unreachable branch if anybody reinstates a producer without the copy.
     */
    case 'skipped':
      /*
       * ⚠️ **NOT A FAILURE, and toned so that nobody could read it as one.**
       * Nothing was sent and nothing went wrong — the same `neutral` surface
       * `settled` uses, no alert glyph, and no action offered, because there is
       * nothing here for anyone to put right.
       *
       * Only reached on a LINKED invoice: the caller drops a skip on an
       * unlinked one entirely, since "this invoice is not in Xero yet" is the
       * answer to every save of every invoice nobody has sent, and a notice on
       * all of them would train people to disregard this strip.
       *
       * ⚠️ It leads with the save, like every other branch here. Xero does not
       * enforce invoice-number uniqueness, so anything a person could read as
       * "my save was lost" is the path to a second demand for money.
       */
      return {
        tone: 'neutral',
        title: 'Saved here. Nothing was sent to Xero this time.',
        body: state.message,
        offerDefaults: false,
        offerIntegrations: false,
      };
    case 'sent': {
      /*
       * ⚠️ Conditional on the invoice not having moved on since — flatly
       * reporting "sent" over a later edit would be the stale "synced" this
       * strip exists to prevent. But `unsent` alone can't decide it: it reads
       * the link's `lastSyncedAt`, a request behind (the re-read hasn't
       * landed yet), so the comparison is against OUR OWN push time instead,
       * known here already. Unparseable instants fall through to success —
       * we saw the push accepted.
       */
      const editedAt = instantOf(updatedAt);
      const pushedAt = instantOf(state.at);
      if (unsent !== null && editedAt !== null && pushedAt !== null && editedAt > pushedAt) {
        return {
          tone: 'warning',
          title: 'Xero has not caught up with the latest edit.',
          /* Same correction as the `idle` branch above — there is no Send button; the save is the send. */
          body: `Sent ${formatDateTime(state.at)}, edited since ${formatDateTime(updatedAt)}. Save it again to send the latest version.`,
          offerDefaults: false,
          offerIntegrations: false,
        };
      }
      /*
       * Worded as the quote page words its `updated`, with the one fact this
       * strip has and that block does not: when.
       */
      return {
        tone: 'success',
        title: 'Saved, and Xero’s copy of this invoice was updated to match.',
        body: `The Xero document was updated at ${formatDateTime(state.at)}.`,
        offerDefaults: false,
        offerIntegrations: false,
      };
    }
    case 'settled':
      /*
       * ⚠️ **THE ORDINARY OUTCOME, and toned as information — this is the one
       * decision on this block that is worth arguing about.** Once anyone
       * approves or sends the invoice from inside Xero its copy leaves DRAFT and
       * every later push is refused, which is the design working and which will
       * be the COMMON answer, not the exception.
       *
       * ⚠️ It read as a warning here until 2026-08-27 — *"the two records will
       * drift apart if this one keeps being edited"* — while the quote page
       * called the identical refusal one document along nothing to worry about.
       * Two pages, one behaviour, opposite tones; the quote page's is the one
       * that is right, and the code that decides it is now shared
       * (`SETTLED_IN_XERO`) so they cannot part again.
       *
       * No send control: it can only reproduce the same 409.
       */
      return {
        tone: 'neutral',
        title: 'Saved here. Xero’s copy was not changed.',
        body: `${state.message} It has moved past draft there, so make any further change in Xero.`,
        offerDefaults: false,
        offerIntegrations: false,
      };
    case 'gone':
      /*
       * Routed through the same `STOOD_DOWN_AS_GONE` model the panel uses, and
       * deliberately NOT repeating its recovery. `XeroPanel` is further down
       * this page, already showing the stood-down link and the "unlink, then
       * create afresh" sequence against the Unlink button that performs it; a
       * second copy here would be one instruction in two places, and the one
       * further from the button is the one that goes stale.
       *
       * (The quote page says "on the panel above" for the same reason. The
       * panel is BELOW the strip here — that word is a fact about this layout,
       * not a difference in what the two pages claim.)
       */
      return {
        tone: 'warning',
        title: 'Saved here. The invoice was deleted in Xero, so there was nothing to update.',
        body: `${state.message} What to do about it is on the Xero panel below.`,
        offerDefaults: false,
        offerIntegrations: false,
      };
    case 'stopped':
      /*
       * TERMINAL for a reason the shared model does not name — today only
       * `XERO_INVOICE_HAS_PAYMENTS` — and the copy has to close the subject
       * rather than leave somebody pressing a button. Money has moved against
       * that document, so unlike `settled` this IS something to act on, which is
       * why it keeps the warning tone and its own sentence.
       */
      return {
        tone: 'warning',
        title: 'Saved here. Xero will not take this change.',
        body: `${state.message} Further changes have to be made in Xero.`,
        offerDefaults: false,
        offerIntegrations: false,
      };
    case 'refused':
      return {
        tone: 'warning',
        title: 'Saved here. Xero’s copy of this invoice was not updated.',
        /* ⚠️ Ended *"Put it right and send again."* until 2026-08-31 — "send" named a button that no longer exists. The save is the send. */
        body: `${state.message} Put it right and save again to send it.`,
        offerDefaults: state.offerDefaults,
        offerIntegrations: state.offerIntegrations,
      };
    case 'unreachable':
      // ⚠️ States the UNCERTAINTY rather than resolving it — the request may well have landed, so this may not assert the change isn't in Xero.
      return {
        tone: 'warning',
        title: 'Saved here. We couldn’t tell whether Xero was updated.',
        body: `${state.message} Check the invoice in Xero before acting on it.`,
        offerDefaults: false,
        offerIntegrations: false,
      };
  }
}

/*
 * The push strip renders through `Notice`, whose `tone` takes every member of
 * `PushNotice['tone']` directly.
 *
 * ⚠️ `info` is produced by no branch since the `sending` case went on
 * 2026-08-31. It stays in the tone union because pruning a TOKEN is not the
 * same job as pruning an affordance. Do not read its presence as a live state.
 */

/* --- page ---------------------------------------------------------------- */

type LoadResult =
  | { kind: 'ready'; invoice: Invoice }
  | { kind: 'notfound' }
  | { kind: 'error'; message: string };

/* -------------------------------------------------------------------------
 * RAISING A CREDIT NOTE FROM THIS INVOICE
 *
 * ✅ **`creditNoteGateOf` IS ONE COPY NOW, IN `@/lib/credit-notes`, AND IS
 * IMPORTED BY THIS PAGE AND BY `InvoicesPage`.** *SUPERSEDED: "🔴 THIS IS A
 * SECOND COPY … IT SHOULD BE ONE COPY IN `src/lib/credit-notes.ts`."* The note
 * asked for the move and named the right home; it was done on 2026-09-15.
 *
 * ✅ **IT ENFORCES THE OWNER'S RULE, AND ITS OWN DOC IS WHERE THAT IS ARGUED.**
 * *SUPERSEDED: "THE RULE IT ENFORCES IS THE SUPERSEDED ONE … not expressible
 * from any client."* An invoice that has been ISSUED may be credited and a
 * cancelled one may not, as the positive `isAwaitingPayment || isPaid`. **Do not
 * re-derive that there and here**: a second copy of an ARGUMENT rots the same
 * way a second copy of a rule does, which is what this block used to say about
 * the other half.
 *
 * ⚠️ **The hook below is STILL duplicated, deliberately.** That module decides
 * and never fetches, so it could not travel with the gate — and it is the cheap
 * half: it carries no wording and no rule, only *has the list arrived*.
 * --------------------------------------------------------------------- */

/**
 * 🔴 **`/invoice-statuses`, NOT `/invoice-tracker/status-labels`** — only the
 * master carries `isAwaitingPayment`, and reading the labels here would not
 * fail: the key would be `undefined` on every row and the gate would silently
 * collapse back to the superseded paid-only rule. No `isActive` filter, because
 * an invoice still carries a status retired after it was raised.
 *
 * `failed` is a THIRD state: loading and failed both have no list and must not
 * say the same thing.
 */
function useInvoiceStatusFlags() {
  const [statuses, setStatuses] = useState<readonly InvoiceStatus[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listInvoiceStatuses({ page: 1, perPage: 200 }, controller.signal)
      .then((answer) => {
        if (controller.signal.aborted) return;
        setStatuses(answer.data);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // Left `null` rather than `[]`: an empty list is a real answer meaning
        // "no statuses exist" and would produce the wrong sentence.
        setFailed(true);
      });
    return () => controller.abort();
  }, []);

  return { statuses, failed };
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { ability, user } = useAuth();

  /*
   * The read gate, computed here because the return that uses it has to sit
   * below every hook. See that return, further down, for the reasoning.
   */
  const canReadModule = ability.canRead('invoices');

  /*
   * ⚠️ **`fromDealId` AND THE BANNER IT DROVE ARE DELETED — 2026-08-30 — and
   * this is a corrected CLAIM, not a removed feature.** *SUPERSEDED: "Did the
   * reader arrive here straight off a deal conversion? A deal line has no tax
   * half to give (`f_deal_products` has no `tax_rate` or `xero_tax_type`
   * column), so every copied line arrives with `0.00` tax — the invoice looks
   * complete and asks for the wrong amount."*
   *
   * The banner read **"No tax rate came across from the deal. … Set one on each
   * line below."** Both sentences are now false, and false in the **reassuring
   * direction's opposite** — they send somebody to correct lines that are
   * already right:
   *
   *  - a copied line's `taxRate` is **seeded from the COMPANY**, through the
   *    same lookup a line create uses;
   *  - and on this route a `0.00` is **unreachable**, because
   *    `COMPANY_HAS_NO_TAX_RATE` refuses the whole conversion — before the
   *    transaction opens — whenever the deal has lines and the company records
   *    no rate. An invoice raised from a deal and carrying any line at all is
   *    one whose company had a rate.
   *
   * **The still-true half went nowhere and is not lost:** `xeroTaxType` is still
   * `null` on a copied line, and `null` means *inherit the company's Xero sales
   * default*, never "no tax". Nothing on this page ever said otherwise, and the
   * line grid below renders that state itself.
   *
   * ⚠️ **The `fromDeal` router state is gone at BOTH ends** — `DealDetailPage`
   * no longer sets it, because its `navigate` went with the `ConfirmDialog`. A
   * reader can still see where an invoice came from: `invoice.deal` is on the
   * record and is rendered as a link, which is a fact about the document rather
   * than about how somebody arrived. **Do not reintroduce the state to hang a
   * new notice on it** without re-measuring what the conversion writes — this
   * banner was correct on the day it shipped and was falsified by a change in
   * another repo, with nothing here able to see it.
   */
  /*
   * Read once, app-wide, by `XeroProvider`. A missing provider degrades to a
   * disconnected reading rather than throwing, so nothing on this page depends
   * on the integration being mounted.
   */
  /*
   * ⚠️ `loading` is no longer read. It gated the deleted `queueXeroPush`, which
   * short-circuited a disconnected tenant rather than pushing at it — a
   * decision the server now makes, and makes better, since it can tell a
   * dropped connection from a reading this page had not finished taking.
   */
  const { connection: xeroConnection } = useXero();

  /*
   * A non-numeric path segment can never match a record, so it is a 404 without
   * asking the API — which would answer 422 for it anyway, and a validation
   * error is not what "/invoices/banana" means to a user.
   */
  const invoiceId = id !== undefined && /^\d+$/.test(id) ? Number(id) : null;

  /*
   * One id-stamped slot for the header rather than separate loading / error /
   * notFound flags, the DeliverableDetailPage idiom: navigating straight from
   * one invoice to another can never flash the previous record's totals under
   * the new id, and "loading" is simply the absence of a result for this id.
   */
  const [result, setResult] = useState<{ id: number; value: LoadResult } | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  /*
   * The basket carries its own loading and error state, stamped with the same
   * id. It is a second request and it can fail on its own; when it does, the
   * header — which is where the money everybody came for actually lives — must
   * still render.
   */
  const [linesResult, setLinesResult] = useState<{
    id: number;
    lines: InvoiceProduct[] | null;
    error: string | null;
  } | null>(null);

  const [editing, setEditing] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  /*
   * The legacy gate is an acknowledgement, not an action: confirming only
   * mounts the editable grid, which has its own "Add row" and save step, so
   * nothing is written by it. ⚠️ In front of the grid rather than on a
   * button, since "Add row" lives inside a component this page doesn't own —
   * same shape `QuoteDetailPage` uses for its hand-entered-totals check.
   */
  const [pendingLegacyAdd, setPendingLegacyAdd] = useState(false);
  const [legacyAcknowledged, setLegacyAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  /*
   * Xero sales defaults are a COMPANY record read from an invoice page: a
   * push is refused with `XERO_NO_TAX_TYPE`/`XERO_NO_LINE_AMOUNT_TYPE` (same
   * codes as a quote push) until the customer carries both, and
   * `XeroSalesDefaultsDialog` needs the whole `Company` (three fields not on
   * `invoice.company`'s bare `{id, name}`). Fetched only when the dialog
   * opens, not on page load.
   */
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [defaultsResult, setDefaultsResult] = useState<{
    id: number;
    company: Company | null;
    error: string | null;
  } | null>(null);
  const [defaultsToken, setDefaultsToken] = useState(0);
  /*
   * What to say once the defaults are saved — NOT a trigger to push again.
   * The person asked for one push; a second firing on its own is an action
   * they didn't take.
   */
  const [defaultsSaved, setDefaultsSaved] = useState<string | null>(null);

  /*
   * ⚠️ Tasks and notes carry NO role guard at all, unlike every other write
   * here — `viewer` may add both, so nothing below consults `ability`.
   *
   * Both live in `ActivityDrawer` behind one header button, which owns the
   * fetching, completion toggle, delete and note composer; this page holds
   * only whether the sheet is open.
   */
  const viewerId = user?.id ?? null;
  const viewerTimeZone = viewerZone(user?.timezone);
  const [activityOpen, setActivityOpen] = useState(false);

  /*
   * What the delete confirmation may promise, read only when that dialog
   * opens — a figure cached at page load would go stale in the direction
   * that understates what's about to be destroyed.
   */
  /** Bumped when credit is applied so the credit summary under Totals refetches. */
  const [creditSummaryKey, setCreditSummaryKey] = useState(0);
  const [attachmentTotals, setAttachmentTotals] = useState<{
    tasks: number | null;
    notes: number | null;
    systemNotes: number;
  }>({ tasks: null, notes: null, systemNotes: 0 });

  /*
   * The header PATCH and every line write take a WRITE role
   * (`requireRole(...WRITE_ROLES)`), so a basic_user and an advanced_user may
   * both edit here; DELETE alone is admin-tier, like every DELETE in the API.
   */
  const canCreate = ability.canCreate('invoices');
  const canUpdate = ability.canUpdate('invoices');
  const canDelete = ability.canDelete('invoices');

  /*
   * Raising a credit note from this invoice.
   *
   * ⚠️ **GATED ON `credit-notes`, NOT ON `invoices`.** They are different
   * modules and the grants come apart — `accounts` holds `credit-notes.create`
   * where `operations` does not — so a borrowed `canCreate('invoices')` would
   * be wrong in BOTH directions. This app has already paid for one borrowed
   * gate: `/integrations` on `companies.update`, which silently handed Xero
   * connect and disconnect to every set that granted a company edit.
   *
   * ⚠️ **`credit-notes` carries no `requireRole` — the permission SET decides,
   * with a scope that narrows** — so this is a real answer rather than a role
   * guess. It is still the optimisation: a grant can change under an access
   * token that lasts days, and the server's refusal is the guarantee.
   */
  const canCreateCreditNote = ability.canCreate('credit-notes');
  const creditDenial = denialReason(ability, 'create', 'credit-notes');
  const creditStatuses = useInvoiceStatusFlags();

  /*
   * ✅ **THE WHOLE FLOW IS ONE MODULE NOW, AND THIS PAGE OWNS NONE OF IT.**
   * *SUPERSEDED: a `creditOpen` / `creditPrecheck` / `creditChecking` /
   * `creditPartial` quartet, an `openCreditNote` and a `copyCreditNoteLines`
   * here, with a byte-for-byte twin in `InvoicesPage` — which both copies said
   * in their own comments was the thing to fix.*
   *
   * ⚠️ **What the two copies decided between them is which lines of a
   * customer's invoice are handed back as money**, on two surfaces one click
   * apart. `useRaiseCreditNote` is where that now lives; read its header before
   * changing anything about the precheck, the line selection or the partial.
   *
   * ⚠️ **The already-credited lookup still runs ON THE PRESS rather than on
   * load** — on load it would cost a request for every reader who opens an
   * invoice and never raises anything, and the count would be whatever was true
   * when the page opened rather than when somebody decides.
   */
  const raiseCredit = useRaiseCreditNote();
  /*
   * ⚠️ DELETE on a line is admin-tier while POST/PATCH are `WRITE_ROLES`, so
   * a `basic_user` can add and edit a line but not remove it — invisible
   * unless the disabled control says so.
   */
  const lineDeleteDenialReason = denialReason(ability, 'delete', 'invoices');

  const loadInvoice = useCallback(
    (signal?: AbortSignal) => {
      if (invoiceId === null) return Promise.resolve();
      const settle = (value: LoadResult) => setResult({ id: invoiceId, value });
      return api
        .getInvoice(invoiceId, signal)
        .then((response) => settle({ kind: 'ready', invoice: response.data }))
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          if (caught instanceof ApiError && caught.status === 404) {
            settle({ kind: 'notfound' });
            return;
          }
          settle({
            kind: 'error',
            message: caught instanceof Error ? caught.message : 'Failed to load this invoice',
          });
        });
    },
    [invoiceId],
  );

  const loadLines = useCallback(
    (signal?: AbortSignal) => {
      if (invoiceId === null) return Promise.resolve();
      // Not paginated: the whole basket arrives at once, which is what totalling
      // it and testing it for the legacy state both need anyway.
      return api
        .listInvoiceProducts(invoiceId, signal)
        .then((response) => setLinesResult({ id: invoiceId, lines: response.data, error: null }))
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setLinesResult({
            id: invoiceId,
            lines: null,
            error: caught instanceof Error ? caught.message : 'Failed to load the line items',
          });
        });
    },
    [invoiceId],
  );

  /*
   * ⚠️ Not optional: a line create/update/delete rewrites the invoice's four
   * header totals from the lines, in the same transaction, and none of the
   * three returns the new header — so the invoice on screen is stale the
   * instant any of them succeeds. Both must go back to the server, every time.
   */
  const refetchAfterLineWrite = useCallback(async () => {
    await Promise.all([loadInvoice(), loadLines()]);
  }, [loadInvoice, loadLines]);

  useEffect(() => {
    if (invoiceId === null) return;
    const controller = new AbortController();
    void loadInvoice(controller.signal);
    void loadLines(controller.signal);
    return () => controller.abort();
  }, [invoiceId, loadInvoice, loadLines, reloadToken]);

  const current = result && result.id === invoiceId ? result.value : null;
  const linesFor = linesResult && linesResult.id === invoiceId ? linesResult : null;
  const lines = linesFor?.lines ?? null;
  const linesError = linesFor?.error ?? null;
  const linesLoading = linesFor === null;

  /*
   * The customer this invoice bills, or null — on the seven Zoho imports every
   * relationship is null, and an invoice with no company is refused by Xero
   * with `XERO_INVOICE_NO_COMPANY` long before the defaults matter.
   */
  const defaultsCompanyId =
    current !== null && current.kind === 'ready' ? (current.invoice.company?.id ?? null) : null;

  /*
   * Nothing is written here before the answer is in: the reset happens in the
   * opener, so this effect only ever sets state asynchronously. Writing the
   * "loading" state from inside the effect is what strands a dialog on a
   * skeleton when the id changes mid-load.
   */
  useEffect(() => {
    if (!defaultsOpen || defaultsCompanyId === null) return;
    const controller = new AbortController();
    void api
      .getCompany(defaultsCompanyId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setDefaultsResult({ id: defaultsCompanyId, company: response.data, error: null });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setDefaultsResult({
          id: defaultsCompanyId,
          company: null,
          error: caught instanceof Error ? caught.message : 'Failed to load the customer record',
        });
      });
    return () => controller.abort();
  }, [defaultsOpen, defaultsCompanyId, defaultsToken]);

  /*
   * The company's sales tax, for the line grid's PREVIEW of a new row — built
   * exactly as `InvoiceFormModal` builds its `companySalesTax`. A created line
   * takes this figure server-side (`input.taxRate ?? companyRate`), so without
   * it the grid previewed tax at 0.00 and read "Xero default" on a row the save
   * then taxed at the company rate.
   *
   * ⚠️ **READ AND NEVER SENT, and never written into a draft.** The grid only
   * previews it; the server owns the figure.
   *
   * ⚠️ **Tagged with the company it was read for**, and **a failed read is
   * simply absent** — which the memo below turns into `{ known: false }`, never
   * a fabricated `0`. Deliberately NOT the Sales-defaults dialog's read above:
   * that one runs only while the dialog is open.
   */
  const [companyTax, setCompanyTax] = useState<{
    companyId: number;
    rate: string | null;
    taxType: string | null;
  } | null>(null);

  useEffect(() => {
    if (defaultsCompanyId === null) return;
    const controller = new AbortController();
    void api
      .getCompany(defaultsCompanyId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setCompanyTax({
          companyId: defaultsCompanyId,
          rate: response.data.xeroSalesTaxRate,
          taxType: response.data.xeroSalesTaxType,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // Unknown, not zero: the memo reads a missing tag as `known: false`.
      });
    return () => controller.abort();
  }, [defaultsCompanyId]);

  const companySalesTax = useMemo<CompanySalesTax>(
    () =>
      companyTax !== null && companyTax.companyId === defaultsCompanyId
        ? { known: true, rate: companyTax.rate, taxType: companyTax.taxType }
        : { known: false },
    [companyTax, defaultsCompanyId],
  );

  /* Same id stamp as every other slot on this page: a result for a different
     company can never be read as this one's. */
  const defaultsFor =
    defaultsResult && defaultsResult.id === defaultsCompanyId ? defaultsResult : null;
  const defaultsCompany = defaultsFor?.company ?? null;
  const defaultsError = defaultsFor?.error ?? null;
  const defaultsLoading = defaultsOpen && defaultsCompanyId !== null && defaultsFor === null;

  function openXeroDefaults() {
    /*
     * Cleared here rather than in the effect, so the effect only ever writes
     * state asynchronously — and so a previous attempt's failure, or the
     * previous save's message, never greets the next one.
     */
    setDefaultsResult(null);
    setDefaultsSaved(null);
    setDefaultsOpen(true);
  }

  function closeXeroDefaults() {
    setDefaultsOpen(false);
  }

  /*
   * The two figures the delete confirmation names, fetched together when it
   * opens; either failing leaves both unknown (`attachmentsSentence` says so
   * plainly). Task figure is the LIST total, not `taskSummary`'s — summary
   * counts only OPEN tasks, and a closed one is destroyed just the same.
   *
   * ⚠️ **`systemCount` is typed as required and can still be absent at
   * RUNTIME** — a server predating it serves the row without the key. The
   * `typeof` test is what keeps that out of the sentence: an absent figure
   * reports NO automatic notes, so the dialog reads exactly as it did before
   * this field existed rather than printing `undefined`. ⛔ It is NOT a `?? 0`
   * over a value the server sent: a real `0` and an absent key are both "say
   * nothing about automatic notes" here, and only the absent one is a guess.
   */
  function loadAttachmentTotals() {
    if (invoiceId === null) return;
    setAttachmentTotals({ tasks: null, notes: null, systemNotes: 0 });
    void Promise.all([
      api.listTasks({ invoiceId, perPage: 1 }),
      api.noteSummary({ target: 'invoice', ids: String(invoiceId) }),
    ])
      .then(([taskPage, notes]) => {
        const summary = notes.data[0] ?? null;
        setAttachmentTotals({
          tasks: taskPage.meta.total,
          notes: summary?.count ?? 0,
          systemNotes:
            summary !== null && typeof summary.systemCount === 'number' ? summary.systemCount : 0,
        });
      })
      .catch(() => setAttachmentTotals({ tasks: null, notes: null, systemNotes: 0 }));
  }

  /*
   * The argument for what is left of this machinery is at the top of the file,
   * under "keeping the LINKED Xero document in step".
   *
   * ⚠️ **CORRECTED 2026-08-30.** It said *"It's four refs rather than state on
   * purpose: each is read at FIRE time, from a timer callback or async
   * continuation, where a value captured at render time would be from two saves
   * ago."* Two of the four remain and there is no timer left to fire from — the
   * server pushes inside the save. They are still refs because the MANUAL
   * re-send reads them from a click continuation.
   */
  /*
   * ID-STAMPED like `result`/`linesResult` above, doing a second job here:
   * navigating to another invoice must not carry the previous one's push state
   * across — a report about invoice 12 standing over invoice 13 is a claim
   * about the wrong document. Stamping the id answers that at RENDER, with no
   * reset effect needed.
   */
  const [autoPushResult, setAutoPushResult] = useState<{ id: number; state: XeroAutoPush } | null>(
    null,
  );
  const autoPush =
    autoPushResult !== null && autoPushResult.id === invoiceId
      ? autoPushResult.state
      : AUTO_PUSH_IDLE;

  /** Stamp every write with the invoice it is about. */
  const setAutoPush = useCallback(
    (state: XeroAutoPush) => {
      if (invoiceId === null) return;
      setAutoPushResult({ id: invoiceId, state });
    },
    [invoiceId],
  );

  /*
   * ⚠️ **THE MANUAL RE-SEND STOOD HERE AND IS DELETED, 2026-08-31.** Three
   * things went together, because none of them means anything without the
   * others: `pushInFlight` (the double-click guard — the server's `pushInvoice`
   * has no per-invoice lock, so two overlapping pushes both write and last one
   * wins silently), `pushIntent` (a ref holding the branded `'update'`-only
   * intent, which was the last thing standing between the button and a SECOND
   * Xero invoice on a tenant where Xero does not enforce invoice-number
   * uniqueness) and `sendToXeroNow` itself.
   *
   * Owner's decision, asked twice: *"remove this Push to Xero button from both
   * invoices and quotes, because auto saving is working."* The server pushes
   * inside every save, so the button was a person doing by hand what the save
   * had already done.
   *
   * They are DELETED rather than left unreferenced: a dead push path reads as a
   * live one to the next person who greps for one, and `noUnusedLocals` would
   * not have caught a ref that a stale JSX branch still touched.
   *
   * 🔴 **THE GAP, AND IT IS NOT SOLVED HERE.** There is now no manual retry for
   * an invoice whose automatic push failed. The retry is to SAVE the invoice
   * again — a save re-pushes. But an invoice whose push failed and which nobody
   * edits again **stays stale in Xero, with nothing on screen offering to
   * resend it.** {@link pushNoticeOf} still SAYS so on every branch; it simply
   * has no control to offer. Accepted on the owner's decision, not overlooked,
   * and reported to him. `POST /xero/create/invoices/{id}` is unchanged and
   * remains the operator recovery path.
   */

  /**
   * A save on this invoice has come back. Show what the SERVER did with Xero
   * while it was saving it.
   *
   * ⚠️ It does NOT re-read the invoice. Every call site already does, and it
   * has to run whether or not there is a report to show — the totals move on a
   * line write with or without Xero.
   *
   * ⚠️ **CORRECTED 2026-08-30 — this was `queueXeroPush()`, and it pushed.**
   * It read:
   *
   * > *"A field on this invoice saved. Bring Xero up to date — eventually,
   * > once, and only if there's already a Xero document to bring up to date.
   * > Called from every path that mutates the invoice, header and LINE alike —
   * > a line write rewrites all four header totals in the same transaction, so
   * > it changes the document Xero holds too. Routing both through here is also
   * > what coalesces a line edit and header edit in one window into a single
   * > push."*
   *
   * All of that is now the SERVER's job and it does it per save rather than per
   * burst — a header edit and a line edit are two saves and two pushes, where
   * this coalesced them into one. That is more Xero calls, and it is the trade
   * the owner took: the push is a property of the system rather than of one
   * screen, so it holds for an importer, a script and any second client too.
   *
   * ⚠️ **`undefined` is an ordinary argument, not a missing one**, and
   * `classifyPushReport` reads it as `idle` — this page's "nothing has been
   * pushed in this session".
   *
   * ⚠️ **CORRECTED 2026-08-30. The example it gave no longer exists.** It read
   * *"Callers that have no report to hand — a save made by a component that
   * does not carry one up — pass nothing"*. Every writer on this page carries
   * one up now, so the two real sources of an `undefined` are different facts:
   *
   * - **an older server, or a rollback**, which omits `xeroPush` entirely;
   * - **`InvoiceLineItemsTable` on a clean run**, which passes `undefined`
   *   DELIBERATELY to mean "say nothing" — every push succeeded or was skipped.
   *   That is a positive statement wearing the same shape as an absence, and it
   *   is why the rule below is worded about the ARGUMENT rather than about
   *   ignorance.
   *
   * The push still happened either way; this page was simply not told an
   * outcome. Anything that starts asserting *"nothing was sent"* from an absent
   * report would be inventing a fact about SEED's accounting system.
   */
  function reportSavedToXero(report: XeroPushReport | undefined) {
    setAutoPush(classifyPushReport(report));
  }

  async function removeInvoice() {
    if (invoiceId === null) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.deleteInvoice(invoiceId);
      navigate('/invoices', { replace: true });
    } catch (caught) {
      // The role check runs before the row lookup, so a 403 says nothing about whether the invoice still exists.
      if (caught instanceof ApiError && caught.status === 403) {
        setActionError(
          denialReason(ability, 'delete', 'invoices') ??
            'Your access changed since this page loaded, so the invoice was not deleted.',
        );
      } else if (caught instanceof ApiError && caught.status === 404) {
        setActionError('This invoice has already been deleted.');
      } else {
        setActionError(caught instanceof Error ? caught.message : 'Could not delete the invoice');
      }
      setBusy(false);
    }
  }

  /*
   * Per-person section layout (`preferences.layouts.invoice`). Called here,
   * above every early return, so the hook order never changes. The defaults
   * reproduce the page as it stood: details, then lines, then the Xero block
   * (push notice + panel + defaults-saved note, which read as one unit). The
   * Zoho-import notice stays fixed above the grid: it qualifies the totals
   * wherever the details panel is moved to.
   */
  const layout = usePageLayout('invoice', [
    { id: 'details', label: 'Invoice details' },
    { id: 'lines', label: 'Line items' },
    { id: 'xero', label: 'Xero' },
  ]);

  /*
   * The read gate, below every hook so the early return cannot change the hook
   * order — and ABOVE the not-found and error branches, deliberately. A reader
   * who may not see invoices must not be told whether this one exists: "Invoice
   * not found" over a record that is merely hidden from them is a wrong answer
   * dressed as a real one.
   *
   * ⚠️ **A detail page needs the same gate as its list.** `/invoices/:id` open
   * while `/invoices` refuses is the same hole one level down, and a detail page
   * is exactly what a stale bookmark or a link in an email points at.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies. And **hiding a page
   * is not refusing a request** — `GET /invoices/:id` answers the same to
   * everybody, so nothing here may be worded as though the record were
   * protected. This is a view control.
   *
   * No back link: `/invoices` answers this reader with the same refusal.
   */
  if (!canReadModule) {
    return (
      <>
        <PageHeader eyebrow="Operations" title="Invoices" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  if (invoiceId === null) {
    return (
      <>
        <PageHeader eyebrow={<BackToInvoices />} title="Invoice not found" />
        <Panel>
          <EmptyState
            icon={<InvoiceIcon />}
            title="That isn’t an invoice reference"
            description="An invoice id is a positive whole number."
            action={
              <Button variant="secondary" size="sm" onClick={() => navigate('/invoices')}>
                Back to invoices
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  if (current === null) {
    return (
      <>
        <PageHeader eyebrow={<BackToInvoices />} title="Invoice" />
        <DetailsSkeleton />
      </>
    );
  }

  if (current.kind === 'notfound') {
    return (
      <>
        <PageHeader eyebrow={<BackToInvoices />} title="Invoice not found" />
        <Panel>
          <EmptyState
            icon={<InvoiceIcon />}
            title="This invoice doesn’t exist"
            description="It may have been deleted by someone else since this link was made."
            action={
              <Button variant="secondary" size="sm" onClick={() => navigate('/invoices')}>
                Back to invoices
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  if (current.kind === 'error') {
    return (
      <>
        <PageHeader eyebrow={<BackToInvoices />} title="Invoice" />
        <Panel>
          <PanelBody className="flex flex-col items-start gap-3">
            <Notice tone="danger" role="alert" title="Couldn’t load this invoice." className="w-full">
              <span className="min-w-0 [overflow-wrap:anywhere]">{current.message}</span>
            </Notice>
            <Button variant="secondary" size="sm" onClick={() => setReloadToken((n) => n + 1)}>
              Try again
            </Button>
          </PanelBody>
        </Panel>
      </>
    );
  }

  const { invoice } = current;

  // `invoice.id` is the id the server actually answered with.
  const activityTarget = { type: 'invoice', id: invoice.id } as const;
  // Blank subject falls back to the Xero number, then the id, since the sheet's subheading is the only thing saying which record it's about.
  const activityLabel =
    invoice.subject.trim() || invoice.xeroInvoiceNumber || `Invoice #${invoice.id}`;
  const lineCount = lines?.length ?? 0;

  /*
   * The legacy trap: seven invoices came in from Zoho with real money and no
   * line items (no product columns in the export). Posting a single line
   * makes the server rewrite all four header totals from that line, same
   * transaction, no confirmation, no undo, imported figures kept nowhere —
   * the API doesn't surface that, so the page does: a standing warning, and a
   * hard confirmation naming the figures.
   *
   * Only knowable once the basket has loaded, so the editable grid is held
   * back while lines are unknown rather than guessed at.
   */
  const legacyImported = lines !== null && isLegacyImported(invoice, lines);

  /*
   * Same four-state gate `QuoteDetailPage` puts in front of its line table:
   * basket LOADING or FAILED (⚠️ mounting before `legacyImported` can be
   * computed is the window where one click destroys the only copy of four
   * figures — a failed load still mounts the grid, but READ-ONLY), or totals
   * imported and not yet acknowledged.
   * ⚠️ The grid does its own basket read regardless — this gate is about what
   * it may WRITE, not what it may show.
   */
  const linesGate: 'loading' | 'unchecked' | 'confirm' | 'open' = linesLoading
    ? 'loading'
    : linesError !== null
      ? 'unchecked'
      : legacyImported && !legacyAcknowledged
        ? 'confirm'
        : 'open';

  // `modifiedBy` is stamped with the creator at INSERT, so its presence says nothing about whether anyone has edited the invoice — the timestamps are the only honest signal.
  const edited = invoice.updatedAt !== invoice.createdAt;
  const overdue = isOverdue(invoice);

  // Every invoice read carries the `xero` envelope, `UNLINKED` rather than absent when there's no link; `linkOf()` supplies `NO_LINK` for a response that predates it.
  const xeroLink: XeroLink = linkOf(invoice);
  // ⚠️ Reported only where both facts are in hand, and names no control — see `changedSinceLastPush` for why, and why the comparison carries a tolerance.
  const unsentChanges = changedSinceLastPush(invoice, xeroLink);
  /*
   * ⚠️ **CORRECTED 2026-08-31 — `renderPushIntent` / `canSendToXero` ARE GONE
   * WITH THE BUTTON.** They read:
   *
   * > *"⚠️ Gated on the same intent the push itself is gated on, not `isLinked`
   * > — a conflicted or archived invoice must not be offered 'Send to Xero now'
   * > over a push `sendToXeroNow` would then decline … ⚠️ But an outcome from
   * > THIS SESSION always survives — a `gone` refusal stands the link down to
   * > `archived` a moment before the re-read lands, so gating on the intent
   * > ALONE would flash 'Saved here. The invoice was deleted in Xero' and then
   * > erase it. `canSend` keeps the button honest in that window; the sentence
   * > stays either way."*
   *
   * **The last clause is the half that survives, and it is now the whole
   * thing:** the SENTENCE stays either way, and there is no button whose
   * honesty needs keeping.
   *
   * ⚠️ **The NARROWING that intent performed is kept, and it had to be
   * re-derived rather than dropped.** `canSendToXero` was false on a
   * `conflict` or `archived` link, which suppressed the idle stale-warning
   * there. Without it that warning would render on exactly those two — telling
   * somebody to *"save it again to send the latest version"* when a save will
   * NOT send it, because the server skips a conflicted or archived link. That
   * is worse than the button it replaced: a dead button is visible, wrong
   * advice is not. `isSilentlyNotSyncing` asks the same question in the
   * vocabulary that now matters — *will a save reach Xero* — and `XeroPanel`
   * below keeps sole ownership of the "Changes are not reaching Xero" copy for
   * that pair, so the instruction stays in one place.
   *
   * `unsentChanges !== null` carries the rest: it already requires a link and a
   * `lastSyncedAt`, so the strip still says nothing about an invoice nobody has
   * ever sent. An outcome from THIS SESSION still survives the `gone`-refusal
   * window through `autoPush.kind !== 'idle'`, unchanged.
   */
  const saveWillReachXero = !isSilentlyNotSyncing(xeroLink);
  /**
   * ⚠️ **A skip on an invoice that is NOT IN XERO draws nothing at all**, and
   * that is the most consequential line in this block.
   *
   * The server answers `skipped` on every save of every invoice nobody has sent
   * to Xero — which is most invoices, most of the time. Rendering it would put
   * a Xero strip on the whole system, and the panel below already says the
   * invoice has no counterpart, in the place that owns that fact.
   *
   * On a LINKED invoice a skip means something else and is worth a quiet line:
   * the server had a document to update and sent nothing — no lines left, say.
   *
   * ⚠️ The split is read off the LINK, never off the message's wording. Which
   * skip it is cannot be told from its sentence, and the link answers it from
   * data already in hand.
   */
  const skipWorthShowing = autoPush.kind !== 'skipped' || xeroLink.id !== null;
  const pushNotice =
    skipWorthShowing && ((unsentChanges !== null && saveWillReachXero) || autoPush.kind !== 'idle')
      ? pushNoticeOf(autoPush, unsentChanges, invoice.updatedAt)
      : null;

  /*
   * `connected` is required alongside `baseCurrency` because `baseCurrency`
   * belongs to the TENANT — a stale reading from a since-dropped connection
   * must never be presented as this org's base currency. Both failures fall
   * into `kind: 'none'`, which renders nothing.
   */
  const baseView = baseCurrencyView(
    invoice,
    xeroConnection.connected ? xeroConnection.baseCurrency : null,
  );
  // Null only under `kind: 'none'`. Spread rather than `undefined`, which `exactOptionalPropertyTypes` refuses.
  const baseRateHint = rateHint(baseView);

  /*
   * Has this invoice's total moved since the copy the customer holds was
   * produced? `null` on every invoice that has never been pushed to Xero, and
   * `null` again the moment it is re-pushed — both correct, and both look
   * exactly like the feature not working. See `src/lib/reprice.ts`.
   *
   * ⚠️ Read straight off the record and nowhere else. In particular the amount
   * carries `repricedFromCurrency`, NOT `invoice.currency`: the currency is an
   * ordinary editable column and can have been changed since the stamp, which
   * would print the old figure under the new code.
   */
  const repriced = repriceNotice(invoice);

  return (
    <>
      <PageHeader
        eyebrow={<BackToInvoices />}
        title={documentDisplayTitle(invoice.subject, invoice.xeroInvoiceNumber)}
        description={
          <>
            <StatusBadge status={invoice.status} />
            {overdue && (
              <Badge tone="danger" dot>
                Overdue
              </Badge>
            )}
            <ImportedFromXeroBadge importedAt={invoice.importedFromXeroAt} />
            <SentToClientBadge sent={invoice.sentToClient} sentAt={invoice.sentToClientAt} />

            {/*
              ⚠️ **The one number here used to be the XERO one, unlabelled** —
              which in the position a reader takes for "this record's number"
              is the exact conflation the References group below exists to
              prevent. EMERGE's own number leads, bare, because it is the id
              and is always present; Xero's follows and is NAMED, because it
              belongs to another system and is absent until a push lands.
            */}
            <span className="text-fg-subtle">·</span>
            <span className="tabular">#{invoice.id}</span>
            {invoice.xeroInvoiceNumber && (
              <>
                <span className="text-fg-subtle">·</span>
                <span className="tabular">Xero {invoice.xeroInvoiceNumber}</span>
              </>
            )}
            {invoice.company && (
              <>
                <span className="text-fg-subtle">·</span>
                <LinkedValue
                  to={`/companies/${invoice.company.id}`}
                  name={documentCustomerName(invoice.company)}
                />
              </>
            )}
            {invoice.dueDate && (
              <>
                <span className="text-fg-subtle">·</span>
                <span>Due {formatDateOnly(invoice.dueDate)}</span>
              </>
            )}
          </>
        }
        actions={
          <>
            {/*
              Tasks and notes lead, and are never gated: the task and note
              routers carry no role guard, so a `viewer` who can open this
              invoice can write against it. The header wraps, so the row folds
              rather than overflowing at 320px.
            */}
            <ActivityButton target={activityTarget} onOpen={() => setActivityOpen(true)} />
            {/* Renders nothing unless the company has spendable credit in this currency. */}
            <ApplyCreditButton
              invoice={invoice}
              onApplied={() => {
                void loadInvoice();
                setCreditSummaryKey((key) => key + 1);
              }}
            />
            {/*
              Ungated for the same reason as the button above: `GET
              /invoices/:id/pdf` carries `authenticate` and no role guard, so a
              `viewer` who can read this invoice may download it. It renders
              NOTHING until the invoice is in Xero — hidden rather than
              disabled, and the gate reads the LINK ROW, never
              `xeroInvoiceNumber`.

              ⚠️ This is the ONE Xero-derived control on this page, and it makes
              no Xero request of its own — the route does. The header comment
              above about this page not talking to Xero still holds.
            */}
            <DownloadPdfButton
              kind="invoice"
              document={invoice}
              reference={invoice.subject}
              variant="detail"
            />
            {/*
              🔴 **RAISE A CREDIT NOTE — the one thing raised FROM an invoice.**
              It sits before Edit because it is the action somebody arrives on
              this page to take once the invoice has been issued, where Edit and
              Delete are maintenance.

              ⚠️ **ALWAYS RENDERED, never hidden** — disabled with its reason
              on `title` wherever `creditNoteGateOf` refuses, **and for a
              reader without `credit-notes.create` too.** A bare `disabled`
              would make "you may not raise these", "this one does not qualify
              yet", "this invoice has no company" and "the statuses could not
              be read" identical, and hiding it would make the feature
              indistinguishable from one that does not exist on this page.

              ⚠️ **That is the opposite treatment from Edit and Delete beside
              it**, which are hidden without the grant. Those are gated on
              `invoices` — the module this page is about — so their absence is
              legible; a credit note is raised from ANOTHER module, and a
              control that silently never appears reads as a feature nobody
              built. The `/companies` "+" incident is the precedent: merely
              unreachable was reported as DELETED.

              🔴 **THE REASON IS ON A WRAPPER `<span>` AND NOT ON THE BUTTON.**
              `Button` carries `disabled:pointer-events-none`, so a disabled
              one never receives the hover and **a `title` on it never renders**
              — the sentence would be in the markup and readable by nobody.
            */}
            {(() => {
              const gate = creditNoteGateOf(
                invoice,
                canCreateCreditNote,
                creditDenial,
                creditStatuses.statuses,
                creditStatuses.failed,
              );
              // A disabled `Button` given a `title` renders the reason as the
              // styled hover label on its own wrapper span — a native `title`
              // on a hand-rolled span would not match the rest of the app.
              return (
                <Button
                  variant="secondary"
                  leadingIcon={<PlusIcon />}
                  disabled={!gate.allowed}
                  loading={raiseCredit.checkingId === invoice.id}
                  onClick={() => raiseCredit.start(invoice)}
                  {...(gate.allowed ? {} : { title: gate.reason })}
                >
                  Raise credit note
                </Button>
              );
            })()}
            {/* A header PATCH takes a write role, the same as the line writes
                below — so Edit follows `canUpdate`, and only Delete is held
                back to the admin tier. */}
            {canUpdate && (
              <Button
                variant="secondary"
                leadingIcon={<PencilIcon />}
                onClick={() => setEditing(true)}
              >
                Edit invoice
              </Button>
            )}
            {canDelete && (
              <Button
                variant="danger"
                iconOnly
                leadingIcon={<TrashIcon />}
                aria-label={`Delete ${invoice.subject}`}
                title={`Delete ${invoice.subject}`}
                onClick={() => {
                  setActionError(null);
                  loadAttachmentTotals();
                  setPendingDelete(true);
                }}
              />
            )}
            <LayoutToolbar layout={layout} />
          </>
        }
      />

      {/*
        ⚠️ `min-w-0` on every panel in this grid is load-bearing: a grid item
        defaults to min-width:auto, so the basket's wide table would widen the
        whole track instead of scrolling in its own TableWrap, overflowing the
        page sideways below 768px (shipped once already on DealDetailPage).
      */}
      <div className="grid gap-4">
        {/*
          ⚠️ **THE "RAISED FROM A DEAL, THEREFORE UNTAXED" PANEL IS DELETED —
          2026-08-30.** *SUPERSEDED, both the note and the panel: "Raised from a
          deal, therefore UNTAXED — leads the page so `Tax 0.00` below isn't read
          as a settled figure … `COMPANY_HAS_NO_TAX_RATE` doesn't make this
          redundant: that guard proves a rate exists on the COMPANY, not that
          it's copied onto any line."* Its heading read **"No tax rate came
          across from the deal."**

          **That last clause is exactly what stopped being true.** A copied line
          now takes its `taxRate` from the company through the same lookup a line
          create uses — so the guard proving a rate exists on the company IS the
          guarantee the panel said it was not, and the panel sent people to
          correct lines that were already right.

          ⚠️ **The half that was never about the rate is untouched and is still
          the reason not to write a replacement casually:** `xeroTaxType` is
          `null` on a copied line, and that means *inherit the company's Xero
          sales default*, never "no tax". The line grid renders that state
          itself. See the `fromDealId` block at the top of this component for the
          full correction and for why the router state went with it.
        */}

        {/* Standing half of the legacy warning — stays on the page so nobody has to hit the confirmation to learn where these numbers came from. */}
        {legacyImported && (
          <Notice
            tone="warning"
            title="These totals came from the Zoho import."
            className="min-w-0"
          >
            <p className="m-0 [overflow-wrap:anywhere]">
              Adding a line item replaces them with figures from the lines. The imported
              ones are not kept.
            </p>
          </Notice>
        )}

        {/*
          The movable sections. `LayoutGrid` renders them in the person's
          saved order; with no saved layout it is the same single column, in
          the same order, as before. Its own `gap-4` matches this one.
        */}
        <LayoutGrid
          layout={layout}
          render={{
            details: () => (
        <Panel className="min-w-0" aria-labelledby="invoice-details-heading">
          <PanelHeader id="invoice-details-heading" title="Invoice details" />

          {/*
            Keyed on the record so a half-typed draft or "Saved" line can't
            carry across to a different invoice; a refresh of the SAME
            invoice keeps the key and whatever the user is mid-way typing.
            Owns subject, status, owner, project, bill date, due date,
            currency, Xero invoice number, customer PO, line amounts,
            branding theme, both tracking overrides and payment wording — no
            longer repeated as read-only rows below. Edit modal stays for
            filling many fields at once, and is the only place the four
            header totals can be typed.

            ⚠️ **CORRECTED 2026-08-30.** This carried a `queueXeroPush()` call
            and a note saying it was REQUIRED here — *"a header edit changes the
            document Xero holds exactly as a line write does … omitting it would
            leave a LINKED invoice silently diverging from Xero, invisible from
            both screens."* The premise was right and it is now the server's to
            satisfy: the PATCH this component makes pushes on its own, so there
            is nothing to call and no way to omit it.

            ⚠️ **CLOSED 2026-08-30. The report is wired.** This paragraph read:

            > *"⚠️ **What IS omitted is the REPORT.** `InlineInvoiceFields`
            > holds the response and does not carry `xeroPush` up, so an inline
            > field edit reaches Xero and says nothing on this page. That is a
            > gap in reporting, not in behaviour … The strip below still catches
            > up on the next read through the stale-version comparison."*

            The prop exists now — `onXeroPush`, the same name, shape and
            contract `InvoiceFormModal` takes — and it reports through
            `reportSavedToXero` into the same strip. The stale-version
            comparison still runs and is still the fallback when nothing was
            reported in this session; it is no longer the only thing an inline
            edit leaves behind.
          */}
          {/*
            ⚠️ **`PanelBody`, not a bare `div`** — see the note on
            `QuoteDetailPage`. Without it these fields sit flush against the
            panel edge while the `PanelHeader` above carries `px-4 py-3`, so
            every label is outdented from its own heading. `PanelBody`'s default
            density is that same padding, so the two cannot drift.
          */}
          <PanelBody key={invoice.id} className="min-w-0">
            <InlineInvoiceFields
              invoice={invoice}
              onSaved={() => void loadInvoice()}
              /*
               * ⚠️ The SAME handler the Edit modal passes, into the same state.
               * A second reader of one wire shape on one page is how the two
               * come to describe the same refusal differently.
               */
              onXeroPush={reportSavedToXero}
            />
          </PanelBody>

          <DetailGroup title="Totals">
            <DetailItem label="Sub total">
              <InvoiceMoney value={invoice.subTotal} currency={invoice.currency} />
            </DetailItem>
            {/* discountTotal/taxTotal are null on every import — "the export never said" is different from a recorded zero, so an em dash, never `?? '0.00'`. */}
            <DetailItem label="Discount total">
              <InvoiceMoney
                value={invoice.discountTotal}
                currency={invoice.currency}
                emptyLabel="Not recorded on this invoice"
              />
            </DetailItem>
            <DetailItem label="Tax total">
              <InvoiceMoney
                value={invoice.taxTotal}
                currency={invoice.currency}
                emptyLabel="Not recorded on this invoice"
              />
            </DetailItem>
            {/* Stored column, not a sum of the lines — what reports and Xero read, and the only value the seven imported invoices have. */}
            <DetailItem label="Grand total">
              <span className="text-base font-semibold text-fg">
                <InvoiceMoney value={invoice.grandTotal} currency={invoice.currency} />
              </span>
              {/*
                Base-currency total, deliberately SECONDARY (smaller, muted),
                matching the quote page. ⚠️ Rendered only in the `converted`
                branch; the other four have no figure and must show none.
                `InvoiceMoney` (not hand-formatting) so it reads as the same
                number in another currency, not a different kind of number.
              */}
              {baseView.kind === 'converted' && (
                <span className="mt-0.5 block text-sm font-normal text-fg-muted">
                  <InvoiceMoney value={baseView.amount} currency={baseView.rate.baseCurrency} />
                </span>
              )}
            </DetailItem>
            {/*
              The rate line sits in Totals, next to the figures it reconciles,
              rather than beside "Currency" below — adjacency is what makes it
              legible. Absent entirely under `kind: 'none'` (already base
              currency, or Xero disconnected): nothing true to say either way.
            */}
            {baseView.kind !== 'none' && (
              <DetailItem
                label="Exchange rate"
                {...(baseRateHint === null ? {} : { hint: baseRateHint })}
              >
                {baseView.kind === 'no-rate' ? (
                  <span className="text-fg-subtle">Not recorded</span>
                ) : baseView.kind === 'unusable-rate' ? (
                  /*
                    ⚠️ The stored value VERBATIM, not through `formatRateLine`
                    — that helper is only total over an `ExchangeRate` built
                    by `exchangeRateOf`, which refused this one; pushing it
                    through would render arithmetic nonsense like
                    "1 GBP = 0 AUD". Shown, not hidden, since "Not recorded"
                    would send someone to enter a rate that's already there.
                  */
                  <span className="tabular [overflow-wrap:anywhere]">{baseView.recorded}</span>
                ) : (
                  <span className="tabular [overflow-wrap:anywhere]">
                    {formatRateLine(baseView.rate)}
                  </span>
                )}
              </DetailItem>
            )}
          </DetailGroup>

          {/* Credit applied, payments received and amount due, under the grand total. Renders nothing when there is nothing to say. */}
          <InvoiceCreditSummary invoiceId={invoice.id} reloadKey={creditSummaryKey} variant="panel" />

          {/*
            ⛔ **NOT helper text, and not to be removed by a copy sweep.** It
            names an irreversible consequence nothing else on this page can
            show: the figure above is not the figure the customer is holding.
            `src/lib/reprice.ts` carries the reasoning and the wording; this
            page renders it and decides nothing.

            It sits directly under the Totals group because the fact is ABOUT
            that grand total — a sentence a screen away from the number it
            qualifies is a sentence nobody joins up.

            ⚠️ **`info`, not `warning`.** Nothing has gone wrong and the invoice
            is correct; what is stale is the copy somebody was sent. A warning
            tone would send the reader hunting a fault in this record. The
            headline sentence is the signal, so the tone is never doing the work
            alone.

            ⚠️ **No re-push control here, deliberately.** Re-pushing rewrites a
            live accounting document. That is its own decision, made from the
            Xero panel below by somebody who went looking for it — not a button
            offered beside a statement of fact.

            ⚠️ **Distinct from the push notice further down**, which reports
            whether Xero has this invoice's LATEST version. This one reports
            that the total itself moved after a customer was sent a figure, and
            it survives a push failing, succeeding or never being attempted.
          */}
          {repriced !== null && (
            <PanelBody className="border-b border-border last:border-b-0">
              <Notice
                tone="info"
                title={<span className="[overflow-wrap:anywhere]">{repriced.headline}</span>}
              >
                <p className="m-0 [overflow-wrap:anywhere]">{repriced.body}</p>
              </Notice>
            </PanelBody>
          )}

          <DetailGroup title="Relationship">
            <DetailItem label="Company">
              <LinkedValue
                to={invoice.company ? `/companies/${invoice.company.id}` : undefined}
                name={invoice.company ? documentCustomerName(invoice.company) : undefined}
              />
            </DetailItem>
            <DetailItem label="Contact">
              <LinkedValue
                to={invoice.contact ? `/contacts/${invoice.contact.id}` : undefined}
                name={invoice.contact?.name}
              />
            </DetailItem>
            {/* The deal embed is keyed `dealName`, not `name`. */}
            <DetailItem label="Deal">
              <LinkedValue
                to={invoice.deal ? `/deals/${invoice.deal.id}` : undefined}
                name={invoice.deal?.dealName}
              />
            </DetailItem>
            {/*
              The presence of the OBJECT says there's a quote — `quoteNumber`
              is nullable, so a numberless quote still links, by id. ⚠️
              "Unassigned" is weaker than it looks: FK is ON DELETE SET NULL
              with nothing else recording provenance, so a deleted quote
              leaves the same null as an invoice never raised from one.
            */}
            <DetailItem label="Raised from">
              <LinkedValue
                to={invoice.quote ? `/quotes/${invoice.quote.id}` : undefined}
                name={
                  invoice.quote ? (invoice.quote.quoteNumber ?? `Quote #${invoice.quote.id}`) : undefined
                }
              />
            </DetailItem>
            {/* Owner has moved into the inline section above, where it is a picker. */}
            {/* The account lead and "Credited to" show in `InlineInvoiceFields` above. */}
          </DetailGroup>

          {/*
            ⚠️ Since migration 067 this is the document-level DEFAULT, not
            what is billed — each LINE names its own project and cities, which
            are what print (see the basket below). The two are frequently
            different sets (one project per product, many products per
            invoice), so this group must never be read as "the cities this
            invoice covers".

            ⚠️ READ-ONLY, and there is no picker anywhere — its absence is the
            decision, not an oversight. A project is chosen PER LINE ITEM in
            the grid below; the header `projectId` survives only as the
            default a new line starts from. `projectId: null` on a PATCH
            CLEARS a live value and answers 200, so a picker here would need
            the same structural guard the quote and deal forms use
            (`projectId` isn't a key on their form values at all).
          */}
          <DetailGroup title="Project (invoice default)" columns="sm:grid-cols-2">
            {/*
              ⚠️ `project: null` does NOT prove this invoice has no project —
              `projects.lookup.ts` swallows its own failures, so an unreachable
              table, a deleted project, and one never set all arrive as the
              same null. Hence "Not recorded here", not `LinkedValue`'s
              confident "Unassigned".
            */}
            <DetailItem label="Project on the invoice header">
              {invoice.project ? (
                <TextLink to={`/projects/${invoice.project.id}`}>
                  {invoice.project.name}
                </TextLink>
              ) : (
                <span className="text-fg-subtle">Not recorded here</span>
              )}
            </DetailItem>
            {/*
              ⚠️ These cities are DERIVED through the invoice's project on
              every request, not stored on the header — a paid invoice from
              March silently reports a different set the moment the project
              is edited. A LINE's cities don't drift: stored on the line, and
              what prints. This header list is only the document default.
              Each city renders with its country.
            */}
            <DetailItem label="Cities on the invoice’s project">
              {invoice.cities.length > 0 ? (
                <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
                  {invoice.cities.map((city) => (
                    <li key={city.id}>
                      <Badge tone="neutral" variant="outline">{cityLabel(city)}</Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-fg-subtle">
                  {invoice.project ? 'This project lists no cities' : '—'}
                </span>
              )}
            </DetailItem>
          </DetailGroup>

          {/*
            Bill date, due date, currency and status all moved into the
            inline section above. Nothing lost: status leads the page header
            as a badge, due date sits beside it with the overdue marker, and
            the exchange rate is in Totals.
          */}

          {/*
            ⚠️ Three references, none a spelling of another: Invoice number
            (OURS, the bare record id — no prefix or padding, since nothing
            in the system issues or reads one back), Xero invoice number (the
            FINANCE SYSTEM's, populated by a successful push — editable, so
            it moved into the inline section above), and Customer PO number
            (the CUSTOMER's, `reference` on the record, pushed to Xero
            verbatim as `Invoice.Reference` — also moved inline, editable).
            What stays here is OURS-and-unwritable (the record id) and the
            reserved GUID below, kept because the column is writable by a
            client and filled by nothing, so a hand-typed value there looks
            authoritative while meaning nothing.
          */}
          <DetailGroup title="References">
            <DetailItem label="Invoice number">
              <span className="tabular">{invoice.id}</span>
            </DetailItem>
            <DetailItem
              label="Xero ID (hand-entered)"
              hint="Nothing fills this — the real link is in the panel below."
            >
              {invoice.xeroInvoiceId ? (
                <span className="font-mono text-xs">{invoice.xeroInvoiceId}</span>
              ) : (
                <span className="text-fg-subtle">Not recorded</span>
              )}
            </DetailItem>
          </DetailGroup>

          <PanelFooter>
            <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
              Created {formatDateTime(invoice.createdAt)}
              {invoice.createdBy ? ` by ${invoice.createdBy.name}` : ''}
            </p>
            {/* modifiedBy is stamped with the creator at insert, so it's NOT an "edited by" marker — comparing timestamps is the only honest test. */}
            <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
              {edited
                ? `Last updated ${formatDateTime(invoice.updatedAt)}`
                : 'Not changed since it was created'}
            </p>
          </PanelFooter>
        </Panel>
            ),
            lines: () => (
              <>

        {/*
          `InvoiceLineItemsTable` fetches the lines, edits in place, saves row
          by row, confirms its own deletes, and brings its own heading — so
          this Panel deliberately has no PanelHeader. ⚠️ It's the SIBLING of
          `QuoteLineItemsTable`, not a shared component — the two must move
          together; read that component's header before changing either.
          `min-w-0` is still load-bearing: a grid item defaults to
          min-width:auto, which would widen the whole track instead of
          letting the wide row scroll inside.
        */}
        <Panel className="min-w-0">
          <PanelBody>
            {linesGate === 'loading' ? (
              // The page's OWN basket read, not the grid's — held back until it lands because `legacyImported` needs it (see `linesGate`).
              <div className="grid gap-2" aria-busy="true">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
              </div>
            ) : linesGate === 'confirm' ? (
              // Hard half of the legacy guard, in front of the grid rather than on its "Add row" button, which lives in a component this page doesn't own.
              <div className="grid gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-fg">Line items</h3>
                  <p className="m-0 mt-1 text-xs text-fg-muted [overflow-wrap:anywhere]">
                    None yet. Adding one replaces the imported totals with figures from the
                    lines.
                  </p>
                </div>
                {canCreate ? (
                  <div>
                    <Button
                      variant="secondary"
                      size="sm"
                      leadingIcon={<PlusIcon />}
                      onClick={() => {
                        setActionError(null);
                        setPendingLegacyAdd(true);
                      }}
                    >
                      Add line items
                    </Button>
                  </div>
                ) : (
                  <p className="m-0 text-xs text-fg-muted">
                    {denialReason(ability, 'create', 'invoices') ??
                      'You cannot add lines to this invoice.'}
                  </p>
                )}
              </div>
            ) : (
              <>
                {linesGate === 'unchecked' && (
                  // Basket read failed, so import status is UNKNOWN — grid mounts read-only rather than hidden, or risking the overwrite the check exists to prevent.
                  <Notice
                    tone="danger"
                    role="alert"
                    title="Editing is held back."
                    className="mb-3"
                    action={
                      <Button variant="secondary" size="sm" onClick={() => void loadLines()}>
                        Try again
                      </Button>
                    }
                  >
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                      Couldn&rsquo;t re-read the lines ({linesError}), so adding one could
                      overwrite imported totals.
                    </span>
                  </Notice>
                )}
                <InvoiceLineItemsTable
                  invoiceId={invoice.id}
                  // Header project's cities, already on the record. ⚠️ Not a line's option list — a line's `cityIds` validates against its OWN project's cities; this only names a city the grid has no other record of.
                  cities={invoice.cities}
                  currency={invoice.currency}
                  companySalesTax={companySalesTax}
                  // BOTH abilities, as `QuoteDetailPage` gates its grid — it adds rows as well as editing, so create and update together. Read-only while `linesGate === 'unchecked'` (failed read).
                  canEdit={canCreate && canUpdate && linesGate === 'open'}
                  canDelete={canDelete && linesGate === 'open'}
                  {...(lineDeleteDenialReason === null
                    ? {}
                    : { deleteDenialReason: lineDeleteDenialReason })}
                  /*
                   * ⚠️ **CORRECTED 2026-08-30.** This had two obligations and
                   * now has one. It read: *"Two obligations, one call, fired
                   * once per save run: the refetch rule (a line write rewrites
                   * the four header totals in the same transaction, returning
                   * none of them), and the push (a line write changes the Xero
                   * document too, through the SAME `queueXeroPush()` — a batch
                   * of eight rows debounces into ONE push)."*
                   *
                   * The refetch rule stands, unchanged. The push is the
                   * server's: `POST`/`PATCH /invoices/{id}/products` each push
                   * and each answer `xeroPush`.
                   *
                   * ⚠️ **So a batch of eight rows is now eight pushes, not
                   * one** — the debounce that coalesced them was this page's,
                   * and it is gone. That is the accepted cost of the push being
                   * a property of the system rather than of this screen; the
                   * server takes a per-document lock so they serialise rather
                   * than race.
                   *
                   * ⚠️ **CLOSED 2026-08-30 — their reports ARE surfaced now.**
                   * This read: *"And their reports are not surfaced.
                   * `InvoiceLineItemsTable` does not carry `xeroPush` up, so a
                   * line edit reaches Xero and says nothing here. Wiring it
                   * needs a decision this pass does not own: eight saves
                   * produce eight reports, and which one a single strip should
                   * show is not obvious."*
                   *
                   * The decision was taken and lives on
                   * `InvoiceLineItemsSavedProps.onXeroPush`: **the first
                   * `failed` report of the run, or nothing at all.** Eight
                   * pushes still happen; one refusal is reported.
                   */
                  onLinesChanged={() => void refetchAfterLineWrite()}
                  /*
                   * ⚠️ **THE REPORT, and a SEPARATE prop from the refetch on
                   * purpose.** `onLinesChanged` says the header totals moved,
                   * which is a different fact and fires on a DELETE too — where
                   * the `204` carries no report at all and the grid deliberately
                   * hands up nothing rather than clearing this strip on a push
                   * whose outcome nobody was told.
                   *
                   * `undefined` reaches `classifyPushReport` as `idle`, which is
                   * this page's "nothing has been pushed in this session" — so a
                   * successful or skipped run falls back to the stale-version
                   * reading rather than asserting anything. It never means
                   * "nothing was sent".
                   */
                  onXeroPush={reportSavedToXero}
                />
              </>
            )}
          </PanelBody>
        </Panel>
              </>
            ),
            xero: () => (
              <div className="grid min-w-0 gap-4">
        {/*
          Above the panel rather than inside it, since the panel is another
          agent's file and this is one page's reading, not a shared rule —
          see `changedSinceLastPush` and AUTO-PUSH at the top of this file.

          ⚠️ A save on a LINKED invoice pushes automatically — **on the SERVER,
          inside the save, since 2026-08-30**, not from this page. The button
          below is a real re-send for an edit that predates that or whose push
          was refused. (This paragraph has now been wrong in both directions:
          it once said "Edits never reach Xero on their own", which was struck
          when this page started pushing, and the page has since stopped.)

          One strip, not two, on purpose: a push resolves the stale-version
          reading on its own once the re-read lands, so a separate panel
          would spend the second in between contradicting this one.

          Rendered only on a linked invoice — nothing to be in step with
          otherwise, and a strip about Xero on an unlinked invoice would read
          as an invitation to make one.
        */}
        {pushNotice !== null && (
          /* `status`, never `alert` — nothing here is an error the user made or a failed save, so an assertive live region would be the wrong interruption. */
          <Notice
            tone={pushNotice.tone}
            role="status"
            title={pushNotice.title}
            className="min-w-0"
          >
                  <p className="m-0 [overflow-wrap:anywhere]">{pushNotice.body}</p>
                  {/*
                    Gated on `canUpdate` like Edit — both of these lead to a
                    write. A reader who can't act still gets the SENTENCE.

                    ⚠️ **"Send to Xero now" STOOD HERE AND IS DELETED,
                    2026-08-31.** Its comment read: *"The only manual re-send
                    this app has for a LINKED invoice — `XeroPanel`'s footer
                    offers nothing here but Unlink, and since 2026-08-30 it
                    offers no Create either (the save does that). ⚠️ This can
                    only ever UPDATE: `pushIntent` discards a `'create'`.
                    Withheld after a terminal refusal, where it could only
                    reproduce the same 409."*

                    Owner's decision, asked twice: *"remove this Push to Xero
                    button from both invoices and quotes, because auto saving is
                    working."*

                    🔴 **Nothing replaces it, and that is the known gap.** The
                    body text above now says that saving again is what sends it.
                    An invoice whose push failed and which nobody edits again
                    stays stale in Xero with nothing here offering to resend.

                    The two that remain are NOT pushes: one opens the
                    Sales-defaults dialog on the COMPANY, one navigates to
                    `/integrations`. Each fixes the CAUSE of a refusal; the next
                    save is then what sends the invoice.
                  */}
                  {canUpdate && (pushNotice.offerDefaults || pushNotice.offerIntegrations) && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {pushNotice.offerDefaults && (
                        <Button variant="secondary" size="sm" onClick={openXeroDefaults}>
                          Set Xero sales defaults
                        </Button>
                      )}
                      {pushNotice.offerIntegrations && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate('/integrations')}
                        >
                          Check the Xero connection
                        </Button>
                      )}
                    </div>
                  )}
          </Notice>
        )}

        {/*
          Below the line items (a Xero invoice copies THIS document, not
          something raised from it), above tasks/notes. `onRequestLink` is
          deliberately NOT passed — there's no invoice SEARCH endpoint to
          source a GUID from. No "Send to Xero" either: invoices push inline
          and 409 `XERO_PUSH_IS_INLINE`; the panel already withholds it.

          ⚠️ **AND NO "Create in Xero", AS OF 2026-08-30.** Owner's decision:
          saving an invoice creates it in Xero as well as updating it, so the
          panel withholds the create for quotes and invoices
          (`CREATED_BY_SAVING` in `XeroPanel`) and says in one line that the
          save is what sends it. What is left here is the status, the refusal
          routing, and Unlink. `POST /xero/create/invoices/{id}` is UNCHANGED
          and stays as the operator recovery path.

          ⚠️ **CORRECTED with it.** This ended: *"What the panel CREATES is a
          DRAFT, never an authorised invoice."* The panel creates nothing now —
          but the fact is unchanged and matters more, because the SAVE is what
          creates: `xero-invoice.payload.ts` sets `Status: 'DRAFT'` literally,
          since an approved invoice can't be deleted in Xero, only voided or
          credited.
        */}
        <XeroPanel
          entity="invoices"
          recordId={invoice.id}
          recordName={documentDisplayTitle(invoice.subject, invoice.xeroInvoiceNumber)}
          link={xeroLink}
          connection={xeroConnection}
          // 2026-10-02 (owner): every Xero action is `invoices.send`.
          canManage={ability.canSend('invoices')}
          // Routes a `company-record` refusal at the record that actually has to change — both Sales-defaults codes point here.
          company={invoice.company}
          onFixCompanyDefaults={openXeroDefaults}
          onChanged={() => void loadInvoice()}
        />

        {/*
          ⚠️ The panel above still shows the refusal that opened the dialog
          (nothing has been retried) — this note beside it is the difference
          between a real next step and reading a stale red block as the save
          having failed. (It read *"press Create again"* until 2026-08-30, then
          *"a save, or 'Send to Xero now' on a linked one"* until 2026-08-31.
          Both of those buttons are gone; the next step is a SAVE, on a linked
          invoice and an unlinked one alike.)
        */}
        {defaultsSaved && (
          <Notice tone="success" role="status" className="min-w-0 [overflow-wrap:anywhere]">
            {defaultsSaved}
          </Notice>
        )}
              </div>
            ),
          }}
        />
      </div>

      <ActivityDrawer
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        target={activityTarget}
        recordLabel={activityLabel}
        viewerId={viewerId}
        viewerTimeZone={viewerTimeZone}
      />

      <InvoiceFormModal
        open={editing}
        onClose={() => setEditing(false)}
        invoice={invoice}
        hasLineItems={
          /*
           * `lines === null` means the basket has not loaded, or failed to —
           * not that there are none. Reporting that as false would offer the
           * header-total inputs on an invoice that may well have lines, which
           * is the one direction that loses data. 'unknown' locks them.
           */
          lines === null ? 'unknown' : lineCount > 0
        }
        /*
         * Re-read rather than trusting the returned record. A header PATCH and a
         * line write are two competing writers of the same four totals, and a
         * refetch is the only thing that reconciles them if both happened.
         */
        onSaved={() => {
          setEditing(false);
          void loadInvoice();
        }}
        /*
         * ⚠️ **THE GRID INSIDE THIS FORM SAVES ON ITS OWN, AND THAT RUN USED TO
         * REACH NOTHING ON THIS PAGE.**
         *
         * Open the form, fix the project on two lines, press the grid's own
         * "Save N rows", watch both report success — and the footer Save has
         * correctly greyed out, because there is nothing left to send. The only
         * way out is a button reading **Cancel**, and `onSaved` above never
         * runs on it. Nor on the X, Escape or the overlay. So the page behind
         * the dialog kept its old line rows — *including the project on each
         * one* — and its old header totals, and a manual refresh "fixed" it,
         * which is what makes it read as flaky rather than as a missing read.
         *
         * ⚠️ **It is not only the project.** A line write REWRITES ALL FOUR
         * HEADER TOTALS inside its own transaction and returns none of them, so
         * the grand total on this page is stale too — a wrong figure in a money
         * column, which is the half of this nobody would file as a refresh bug.
         *
         * ⚠️ Firing it UNDER AN OPEN MODAL is deliberate. The page is wrong the
         * instant those lines land; waiting for the close would mean picking one
         * of four exit paths and getting the other three wrong. `formState`
         * inside the form holds its own copy of the invoice, so a reload here
         * cannot disturb what somebody is typing.
         *
         * Same defect `InvoicesPage` documents at its own `handleLinesSaved`,
         * one screen over. `refetchAfterLineWrite` is the answer that already
         * existed here — the page's own grid has called it all along.
         */
        onLinesSaved={() => void refetchAfterLineWrite()}
        /*
         * ⚠️ **The report, never a push.** The PATCH inside the form has
         * already been to Xero by the time this runs; this only carries back
         * what it said. It used to read `queueXeroPush()` — *"on a LINKED
         * invoice this now goes to Xero on its own; unlinked, `queueXeroPush`
         * returns immediately"* — which was a second request this page made.
         *
         * ⚠️ Passing nothing would not stop anything reaching Xero. It would
         * only stop the person being told what happened.
         */
        onXeroPush={reportSavedToXero}
      />

      {/*
        The hard half of the legacy guard. A banner alone is not enough: this is
        a one-way, unconfirmed, unlogged overwrite of the only copy of these
        figures, so the dialog names them at the moment of the decision.
      */}
      <ConfirmDialog
        open={pendingLegacyAdd}
        onClose={() => setPendingLegacyAdd(false)}
        onConfirm={() => {
          setPendingLegacyAdd(false);
          // Acknowledging MOUNTS the editable grid; it does not add a row — nothing is written until the person enters and saves a line.
          setLegacyAcknowledged(true);
        }}
        title="Replace the imported totals?"
        description={
          <>
            <p className="m-0">
              Adding a line replaces these imported totals with figures from the lines.
            </p>
            <ul className="m-0 mt-3 list-none space-y-1 rounded-md border border-border bg-surface-sunken px-3 py-2 text-sm">
              <li className="flex items-baseline justify-between gap-4">
                <span className="text-fg-muted">Sub total</span>
                <Money
                  amount={invoice.subTotal}
                  currency={invoice.currency}
                  className="font-medium text-fg"
                />
              </li>
              <li className="flex items-baseline justify-between gap-4">
                <span className="text-fg-muted">Discount total</span>
                {invoice.discountTotal === null ? (
                  <span className="tabular font-medium text-fg">Not recorded</span>
                ) : (
                  <Money
                    amount={invoice.discountTotal}
                    currency={invoice.currency}
                    className="font-medium text-fg"
                  />
                )}
              </li>
              <li className="flex items-baseline justify-between gap-4">
                <span className="text-fg-muted">Tax total</span>
                {invoice.taxTotal === null ? (
                  <span className="tabular font-medium text-fg">Not recorded</span>
                ) : (
                  <Money
                    amount={invoice.taxTotal}
                    currency={invoice.currency}
                    className="font-medium text-fg"
                  />
                )}
              </li>
              <li className="flex items-baseline justify-between gap-4">
                <span className="text-fg-muted">Grand total</span>
                <Money
                  amount={invoice.grandTotal}
                  currency={invoice.currency}
                  className="font-semibold text-fg"
                />
              </li>
            </ul>
            <p className="m-0 mt-3">
              The imported figures cannot be recovered.
            </p>
          </>
        }
        confirmLabel="Add line items anyway"
      />

      <ConfirmDialog
        open={pendingDelete}
        onClose={() => {
          setPendingDelete(false);
          setActionError(null);
        }}
        onConfirm={() => void removeInvoice()}
        title={`Delete ${invoice.subject}?`}
        /*
         * Deleting an invoice cascades its lines away with no 409 and no count
         * from the API, so the confirmation names the number — the lines are
         * already loaded here, and this is the only place the user will see it.
         */
        description={
          <>
            <p className="m-0">
              {lines === null
                ? 'This permanently deletes the invoice and any line items on it.'
                : lineCount === 0
                  ? 'This invoice has no line items.'
                  : `This also deletes the ${pluralise(lineCount, 'line item')} on the invoice.`}
            </p>
            {/* Tasks and notes cascade with the invoice like the lines do; the API names neither, so they're named here. */}
            <p className="m-0 mt-2">
              {attachmentsSentence(
                attachmentTotals.tasks,
                attachmentTotals.notes,
                attachmentTotals.systemNotes,
              )}
            </p>
            {/* The delete never reaches Xero (backend `deleteInvoice`): the Xero invoice and its link survive. */}
            <p className="m-0 mt-2">
              If this invoice is in Xero, it stays there. Only the SEED EMERGE record is deleted,
              and that cannot be undone.
            </p>
          </>
        }
        confirmLabel="Delete invoice"
        loading={busy}
        error={actionError}
      />

      {/*
        Three separate states, not one dialog with blanks: it needs a whole
        `Company` (three fields absent from `invoice.company`'s bare
        `{id, name}`), so loading and failure get their own surface.
      */}
      {defaultsCompany !== null ? (
        <XeroSalesDefaultsDialog
          open={defaultsOpen}
          company={defaultsCompany}
          // `missing` omitted: the panel doesn't report which code came back, so the dialog derives it from the company's own nulls instead of guessing.
          context={
            <>
              Opened from{' '}
              {invoice.xeroInvoiceNumber
                ? `invoice ${invoice.xeroInvoiceNumber}`
                : invoice.subject}
              .
            </>
          }
          onClose={closeXeroDefaults}
          /*
           * NO AUTO-RETRY, deliberately — a second push firing by itself is
           * an action nobody took. Saved company is written back before
           * closing so the dialog doesn't flip to its loading surface on the
           * way out.
           */
          onSaved={(saved) => {
            setDefaultsResult({ id: saved.id, company: saved, error: null });
            setCompanyTax({
              companyId: saved.id,
              rate: saved.xeroSalesTaxRate,
              taxType: saved.xeroSalesTaxType,
            });
            setDefaultsOpen(false);
            /*
             * ⚠️ **ONE SENTENCE NOW, AND THE `isLinked` BRANCH IS GONE —
             * 2026-08-31.** It read:
             *
             * > *"⚠️ Two different next steps, and the UNLINKED half CHANGED ON
             * > 2026-08-30. It read: 'Create it in Xero from the panel above.'
             * > There is no Create button on an invoice any more — the save
             * > sends it … The linked half is untouched: 'Send to Xero now' is
             * > a real button above and is still the right next step."*
             *
             * **That button was removed on the owner's decision**, so the
             * linked half pointed at a control the reader would not find —
             * exactly the failure the 2026-08-30 note describes for the other
             * half, one release later. Both halves are now the same act, so
             * they are the same sentence: a branch whose two arms say the same
             * thing is a branch waiting to be edited on one arm only.
             */
            setDefaultsSaved(
              `${saved.name} now carries its Xero sales defaults. Save this invoice again to send it to Xero.`,
            );
          }}
        />
      ) : (
        <Modal
          open={defaultsOpen}
          onClose={closeXeroDefaults}
          title="Xero sales defaults"
          size="sm"
          footer={
            <Button variant="secondary" onClick={closeXeroDefaults}>
              Close
            </Button>
          }
        >
          {defaultsCompanyId === null ? (
            // Not reachable from the refusal this dialog answers (a companyless invoice hits `XERO_INVOICE_NO_COMPANY` first), but the opener is a plain button, so this state still needs a sentence.
            <p className="m-0 text-sm text-fg-secondary">
              No company on this invoice — set one first.
            </p>
          ) : defaultsLoading ? (
            <div className="grid gap-2" aria-busy="true">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : (
            <div className="space-y-3">
              <Notice
                tone="danger"
                role="alert"
                title={<>Couldn&rsquo;t load the customer record.</>}
              >
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  {defaultsError ?? 'The request didn’t complete.'}
                </span>
              </Notice>
              <p className="m-0 text-xs text-fg-muted">Nothing was changed.</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    // Cleared here, not in the effect, or the failure sits on screen through the whole retry, reading as an instant failure.
                    setDefaultsResult(null);
                    setDefaultsToken((token) => token + 1);
                  }}
                >
                  Try again
                </Button>
                {invoice.company && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(`/companies/${invoice.company?.id}`)}
                  >
                    Open {invoice.company.name}
                  </Button>
                )}
              </div>
            </div>
          )}
        </Modal>
      )}

      {/*
        🔴 **THE WHOLE RAISE-A-CREDIT-NOTE FLOW — the precheck warning, the
        form with its line picker, and the partial report.** All three used to
        be written out here and again in `InvoicesPage`; `useRaiseCreditNote`
        owns them now, so the two surfaces cannot word them differently or
        disagree about which lines a customer is credited for.

        ⚠️ **Mounted ONCE, at page level.** The trigger is up in the header and
        calls `raiseCredit.start`; these dialogs have to sit outside it.
      */}
      {raiseCredit.dialogs}
    </>
  );
}
